// ===============================
// src/OngletGardesDesiderata.jsx
// ===============================
// Interface Responsable – Gardes / Désidérata (page dédiée)
//
// COMPOSANT UI UNIQUEMENT — l'algorithme de génération des gardes
// est dans un fichier dédié par secteur :
//   - PR2 / PR1 / OPH : algo-gardes-pr2.js
//   - Astreinte Thoracique : algo-astreintes-thoraciques.js
//
//  - Affichage de la grille des souhaits pour un mois sélectionnable (multi-mois)
//  - Lecture des désidérata via backend : GET /api/v2/desiderata-* (PR2, PR1, OPH)
//  - Les valeurs de souhait sont stockées sous la forme { date: { code, label } }
//  - Bouton Retour vers /responsable/gardes
//  - Boutons : générer / ouvrir la liste de gardes
//  - ✅ Support multi-mois : sélecteur basé sur GET /api/v2/config/desiderata-* (cfg.mois_ouverts)
//
// ===============================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import LogoutButton from "./components/LogoutButton.jsx";
import Pr2EquilibrageResult from "./Pr2EquilibrageResult.jsx";
import Pr2NextDayWarnings from "./Pr2NextDayWarnings.jsx";
import usePr2GenerationChoices from "./usePr2GenerationChoices.js";
import { useAuth } from "./contexts/AuthContext.jsx";
import { canManageGuardList } from "./ui/juniorListAccess.js";
import { apiFetch, medecinsPayloadToArray } from "./apiMedecins.js";
import {
  genererAstreintesThoraciques,
  preparerDonnees,
  phase1_attribuerBlocs,
  phase2_attribuerNguyen,
  phase3_joursRestants,
  phase4_reequilibrage,
  medKey,
} from "./algo-astreintes-thoraciques.js";
import {
  preparerDonnees as preparerDonneesPR2,
  phase1_uneGardeChacun,
  phase2_deuxiemeGarde,
  generationPr2EngagementProgressif,
  generationPr2EquilibragePermanence,
  crossCheckGardes,
  crossCheckGardesTyped,
  MIN_ECART_GARDES_PR2,
  PR2_ASTREINTE_ECART_FIXE,
  prefLabel as prefLabelPR2,
  jourNomCourt,
  isWeekend as isWeekendPR2,
} from "./algo-gardes-pr2.js";
import { confirmDialog, notify } from "./ui/dialogService.js";
import { collapseResponsibleSelfBack } from "./responsableBack.js";

export default function OngletGardesDesiderata() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeGarde = (searchParams.get("type") || "PR2").trim(); // PR1, PR2 ou ASTREINTE_THORACIQUE
  const canConstituteList = canManageGuardList(user, typeGarde);
  const lockSecteur = searchParams.get("lockSecteur") === "1";
  const isAstreinteThoracique = typeGarde.toUpperCase() === "ASTREINTE_THORACIQUE";
  /** PR2 en prod : l’URL peut varier en casse (`type=pr2`). */
  const isPr2Secteur = typeGarde.toUpperCase() === "PR2";
  const withCurrentBack = (path) => {
    if (typeof window === "undefined") return path;
    const params = new URLSearchParams(window.location.search || "");
    const rawBack = collapseResponsibleSelfBack(params.get("back") || "");
    let next = path;
    if (lockSecteur && !/[?&]lockSecteur=/.test(next)) {
      const sep = next.includes("?") ? "&" : "?";
      next = `${next}${sep}lockSecteur=1`;
    }
    if (rawBack) {
      try {
        const backUrl = new URL(rawBack, window.location.origin);
        const targetUrl = new URL(next, window.location.origin);
        if (
          backUrl.pathname === targetUrl.pathname &&
          (backUrl.searchParams.get("type") || "") === (targetUrl.searchParams.get("type") || "")
        ) {
          return `${backUrl.pathname}${backUrl.search}${backUrl.hash}`;
        }
      } catch {
        // Conserver le comportement historique si l'URL n'est pas exploitable.
      }
    }
    if (rawBack && !/[?&]back=/.test(next)) {
      const sep = next.includes("?") ? "&" : "?";
      next = `${next}${sep}back=${encodeURIComponent(rawBack)}`;
    }
    return next;
  };
  const gardesHubPath = () => withCurrentBack(typeGarde ? `/responsable/gardes?type=${encodeURIComponent(typeGarde)}` : "/responsable/gardes");

  // ✅ Label lisible pour le type d'activité
  const typeLabel = useMemo(() => {
    switch (typeGarde) {
      case "PR1": return "Port Royal 1";
      case "PR2": return "Port Royal 2";
      case "ASTREINTE_THORACIQUE": return "Astreinte Thoracique";
      case "OPH": return "Ophtalmologie";
      case "INTERNE1": return "Interne Port Royal 1";
      case "INTERNE2": return "Interne Port Royal 2";
      case "DIGESTIF": return "Garde Senior Digestif";
      case "REANIMATION": return "Garde Réanimation";
      default: return typeGarde;
    }
  }, [typeGarde]);

  // ✅ URL de l'API des médecins selon le type
  const medecinsApiUrl = useMemo(() => {
    switch (typeGarde) {
      case "PR1": return "/api/v2/medecins-gardes-pr1";
      case "PR2": return "/api/v2/medecins-gardes-pr2";
      case "ASTREINTE_THORACIQUE": return "/api/v2/medecins-astreintes-thoracique";
      case "OPH": return "/api/v2/medecins-oph";
      case "INTERNE1": return "/api/v2/medecins-gardes-interne1";
      case "INTERNE2": return "/api/v2/medecins-gardes-interne2";
      case "DIGESTIF": return "/api/v2/medecins-gardes-digestif";
      case "REANIMATION": return "/api/v2/medecins-gardes-reanimation";
      default: return "/api/v2/medecins";
    }
  }, [typeGarde]);

  const [data, setData] = useState([]);
  const [canManageDesiderata, setCanManageDesiderata] = useState(false);
  const [error, setError] = useState(null);
  const [missingMedecins, setMissingMedecins] = useState([]);
  const [medecins, setMedecins] = useState([]);
  const [hasListeGarde, setHasListeGarde] = useState(false);

  // ✅ Ajustements bonus/malus pour astreintes thoraciques
  const [ajustements, setAjustements] = useState({});
  const [savedAjustements, setSavedAjustements] = useState({}); // copie serveur pour détecter les modifs
  const [showAjustements, setShowAjustements] = useState(false);
  const [ajustementsLoaded, setAjustementsLoaded] = useState(false);

  // ✅ Workflow en 3 phases pour astreintes thoraciques
  // thoPhase: 0=pas commencé, 1=revue phase1 (blocs), 12=après jours restants (avant swaps), 2=récapitulatif, 3=terminé
  const [thoPhase, setThoPhase] = useState(0);
  const [thoPrepData, setThoPrepData] = useState(null);
  const [thoPhase1Result, setThoPhase1Result] = useState(null);
  const [thoPhase1Selections, setThoPhase1Selections] = useState({}); // { blocIndex: choisiKey }
  const [thoPhase2Result, setThoPhase2Result] = useState(null);
  const [thoPhase2Selections, setThoPhase2Selections] = useState({}); // { mois: choisiDate }
  const [thoAllMoisCles, setThoAllMoisCles] = useState([]);
  const [thoPhase3Allocations, setThoPhase3Allocations] = useState(null); // allocations complètes après P2 (avant rééquilibrage)
  const [thoPhase3Stats, setThoPhase3Stats] = useState(null); // { totalAttribue, totalNonAttribue, total }
  const [thoPhase4Allocations, setThoPhase4Allocations] = useState(null); // allocations après rééquilibrage (Phase 3)
  const [thoPhase4NbSwaps, setThoPhase4NbSwaps] = useState(0); // nombre de swaps effectués
  /** Après phase3_joursRestants, avant phase4_reequilibrage (pour proposer Phase 3 ou liste sans swaps). */
  const [thoJoursRestantsPack, setThoJoursRestantsPack] = useState(null); // { p3, ajustementsData }
  const thoRecapRef = useRef(null); // ref pour capturer les tableaux récapitulatifs en PDF (page 1)
  const thoDesidRef = useRef(null); // ref pour capturer les desiderata en PDF (page 2)
  const pr2PdfRefs = [useRef(null), useRef(null), useRef(null), useRef(null)]; // 4 pages PDF PR2

  // ✅ Workflow interactif PR2 (et PR1/OPH) en phases
  // pr2Phase: 0=pas commencé, 1=revue phase1 (1 garde chacun), 2=revue phase2 (2e garde), 3=suite
  const [pr2Phase, setPr2Phase] = useState(0);
  const [pr2PrepData, setPr2PrepData] = useState(null);
  const [pr2Phase1Result, setPr2Phase1Result] = useState(null);
  const [pr2Phase2Result, setPr2Phase2Result] = useState(null);
  const [pr2CrossCheck, setPr2CrossCheck] = useState(null); // résultat croisement PR2 vs PR1/Digestif
  const [pr2ExternalLists, setPr2ExternalLists] = useState(null); // listes PR1/Digestif pour croisement algo
  const [pr2ExternalDiagnostics, setPr2ExternalDiagnostics] = useState([]);

  const [cfg, setCfg] = useState(null);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [selectedMoisCle, setSelectedMoisCle] = useState("");

  const [loading, setLoading] = useState(true);
  const [nbGardesSavingKey, setNbGardesSavingKey] = useState(null);
  const [equilibrageBusy, setEquilibrageBusy] = useState(false);
  const equilibrageRun = useRef(0);

  const invalidatePr2Generation = () => {
    if (!isPr2Secteur) return;
    equilibrageRun.current += 1;
    setPr2Phase(0);
    setPr2Phase1Result(null);
    setPr2Phase2Result(null);
    setPr2PrepData(null);
    setPr2CrossCheck(null);
    setPr2ExternalLists(null);
    setPr2ExternalDiagnostics([]);
  };

  // ===== utils =====
  const pad2 = (n) => String(n).padStart(2, "0");

  const scrollToRappelPr2Doc = useCallback((e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const el = document.getElementById("rappel-pr2-doc");
    if (el && "open" in el) el.open = true;
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const getDesiderataEndpoint = (moisCle = MOIS_CLE) => {
    let endpoint = `/api/v2/desiderata-pr2/${moisCle}`; // PR2 par défaut
    if (typeGarde === "PR1") endpoint = `/api/v2/desiderata-pr1/${moisCle}`;
    else if (typeGarde === "ASTREINTE_THORACIQUE") endpoint = `/api/v2/desiderata-astreinte-thoracique/${moisCle}`;
    else if (typeGarde === "OPH") endpoint = `/api/v2/desiderata-oph/${moisCle}`;
    else if (typeGarde === "INTERNE1") endpoint = `/api/v2/desiderata-interne1/${moisCle}`;
    else if (typeGarde === "INTERNE2") endpoint = `/api/v2/desiderata-interne2/${moisCle}`;
    else if (typeGarde === "DIGESTIF") endpoint = `/api/v2/desiderata-digestif/${moisCle}`;
    else if (typeGarde === "REANIMATION") endpoint = `/api/v2/desiderata-reanimation/${moisCle}`;
    return endpoint;
  };

  const saveNbGardesSouhaite = async (med) => {
    if (!canManageDesiderata) return;
    const key = `${med.nom}_${med.prenom}`;
    const raw = Number(med.nbGardes);
    const nbGardes = Number.isFinite(raw) ? Math.max(0, Math.round(raw)) : 0;
    try {
      setNbGardesSavingKey(key);
      const endpoint = getDesiderataEndpoint(MOIS_CLE).replace(`/${MOIS_CLE}`, "");
      const res = await apiFetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mois: MOIS_CLE,
          medecin: { nom: med.nom, prenom: med.prenom },
          souhaits: { nbGardes, valeurs: med.souhaits || {} },
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Erreur HTTP ${res.status}`);
      }
      setData((prev) =>
        prev.map((m) =>
          m.nom === med.nom && m.prenom === med.prenom
            ? { ...m, nbGardes }
            : m
        )
      );
    } catch (err) {
      console.error("❌ Erreur sauvegarde nb gardes:", err);
      notify(`❌ Erreur mise à jour Nb gardes pour ${med.fullName}:\n${err.message}`);
    } finally {
      setNbGardesSavingKey(null);
    }
  };

  const normalizeKey = (str) =>
    String(str || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/_/g, "");

  const buildGapSincePreviousByDate = (jours, affectations, prevWeekMap = null, options = {}) => {
    if (options.detailed) {
      const activitiesByMed = {};
      const addActivity = (med, date, secteur, type) => {
        if (!med || !/^\d{4}-\d{2}-\d{2}$/.test(String(date || ""))) return;
        const medKey = normalizeKey(med);
        if (!medKey) return;
        if (!activitiesByMed[medKey]) activitiesByMed[medKey] = [];
        activitiesByMed[medKey].push({ date, secteur, type });
      };
      const addMap = (map, secteur, type) => {
        if (!map || typeof map !== "object") return;
        for (const [date, med] of Object.entries(map)) {
          if (!date || date.startsWith("_") || !med) continue;
          addActivity(med, date, secteur, type);
        }
      };

      addMap(prevWeekMap, "Garde PR2", "garde");
      addMap(options.externalLists?.pr1Liste, "Garde PR1", "garde");
      addMap(options.externalLists?.digestifListe, "Garde Digestif", "garde");
      addMap(options.externalLists?.reanimationListe, "Garde Réanimation", "garde");
      addMap(options.externalLists?.thoraciqueListe, "Astreinte Thoracique", "astreinte");
      addMap(options.externalLists?.orthopedieListe, "Astreinte Orthopédie", "astreinte");
      addMap(options.externalLists?.previousExternalLists?.pr1, "Garde PR1", "garde");
      addMap(options.externalLists?.previousExternalLists?.digestif, "Garde Digestif", "garde");
      addMap(options.externalLists?.previousExternalLists?.reanimation, "Garde Réanimation", "garde");
      addMap(options.externalLists?.previousExternalLists?.thoracique, "Astreinte Thoracique", "astreinte");
      addMap(options.externalLists?.previousExternalLists?.orthopedie, "Astreinte Orthopédie", "astreinte");

      const details = {};
      for (const d of jours) {
        const med = affectations?.[d] || null;
        if (!med) {
          details[d] = null;
          continue;
        }
        const medKey = normalizeKey(med);
        const previous = (activitiesByMed[medKey] || [])
          .filter((a) => a.date < d)
          .sort((a, b) => b.date.localeCompare(a.date))[0] || null;
        if (!previous) {
          details[d] = null;
        } else {
          const prevMs = new Date(`${previous.date}T00:00:00Z`).getTime();
          const curMs = new Date(`${d}T00:00:00Z`).getTime();
          details[d] = {
            ...previous,
            ecart: Math.round((curMs - prevMs) / 86400000),
          };
        }
        addActivity(med, d, "Garde PR2", "garde");
      }
      return details;
    }

    const prevByMed = {};
    if (prevWeekMap && typeof prevWeekMap === "object") {
      const seedRows = Object.entries(prevWeekMap)
        .filter(([d, m]) => /^\d{4}-\d{2}-\d{2}$/.test(d) && !!m)
        .sort(([a], [b]) => a.localeCompare(b));
      for (const [d, med] of seedRows) {
        prevByMed[med] = d; // garde la plus récente du seed
      }
    }
    const gaps = {};
    for (const d of jours) {
      const med = affectations?.[d] || null;
      if (!med) {
        gaps[d] = null;
        continue;
      }
      const prev = prevByMed[med] || null;
      if (!prev) {
        gaps[d] = null; // 1ère garde connue
      } else {
        const prevMs = new Date(`${prev}T00:00:00Z`).getTime();
        const curMs = new Date(`${d}T00:00:00Z`).getTime();
        gaps[d] = Math.round((curMs - prevMs) / 86400000);
      }
      prevByMed[med] = d;
    }
    return gaps;
  };

  const moisOuverts = useMemo(() => {
    const arr = Array.isArray(cfg?.mois_ouverts) ? cfg.mois_ouverts : [];
    if (arr.length > 0) return arr;

    // fallback legacy : un seul mois
    if (cfg?.mois_cle) {
      return [
        {
          annee: cfg.annee,
          mois_index: cfg.mois_index,
          mois_label: cfg.mois_label,
          mois_cle: cfg.mois_cle,
          deadline_iso: cfg.deadline_iso,
          deadline_affichage: cfg.deadline_affichage,
        },
      ];
    }
    return [];
  }, [cfg]);

  // Mois sélectionné (objet)
  const selectedMonth = useMemo(() => {
    if (!selectedMoisCle) return null;
    return (
      moisOuverts.find((m) => String(m.mois_cle) === String(selectedMoisCle)) ||
      null
    );
  }, [moisOuverts, selectedMoisCle]);

  // Paramètres dérivés du mois sélectionné
  const YEAR = Number(selectedMonth?.annee);
  const MONTH_INDEX = Number(selectedMonth?.mois_index); // 0=janv, 1=fév, ...
  const MONTH_NUM = Number.isFinite(MONTH_INDEX) ? MONTH_INDEX + 1 : NaN; // 1..12
  const MOIS_LABEL = String(selectedMonth?.mois_label || "");
  const MOIS_CLE = String(selectedMonth?.mois_cle || "");
  const generationChoices = usePr2GenerationChoices(isPr2Secteur ? user?.id : null, MOIS_CLE);

  useEffect(() => {
    equilibrageRun.current += 1;
    return () => { equilibrageRun.current += 1; };
  }, [MOIS_CLE, typeGarde]);

  /** Dernières affectations PR2 connues (workflow classique ou engagement progressif). */
  const getPr2AffectationsLesPlusRecentes = useCallback(() => {
    return (
      pr2Phase2Result?.affectations ||
      pr2Phase1Result?.affectations ||
      null
    );
  }, [pr2Phase2Result, pr2Phase1Result]);

  /** Enregistre la liste brute puis ouvre l'éditeur ; confirm avec nombre de jours non attribués. */
  const enregistrerPr2BruteEtOuvrir = useCallback(
    async (opts = {}) => {
      const titrePhase = opts.titrePhase || "";
      const liste = getPr2AffectationsLesPlusRecentes();
      if (!MOIS_CLE) {
        notify("Sélectionnez un mois.");
        return;
      }
      if (!liste || typeof liste !== "object") {
        notify("Aucune affectation PR2 à enregistrer. Lancez d'abord une phase de génération.");
        return;
      }
      const jours = pr2PrepData?.jours || [];
      const nbTrous = jours.filter((d) => !liste[d]).length;
      const intro = titrePhase
        ? `Générer la liste de garde — ${titrePhase} (${MOIS_CLE}) ?`
        : `Générer la liste de garde pour ${MOIS_CLE} ?`;
      const detailTrous =
        nbTrous > 0
          ? `\n\n⚠️ ${nbTrous} jour(s) sans médecin assigné (trous). Vous pourrez compléter dans l'éditeur liste brute.`
          : "\n\nTous les jours du mois ont une attribution.";
      if (!(await confirmDialog(intro + detailTrous))) return;
      try {
        const saveRes = await apiFetch("/api/v2/liste_gardes/enregistrer_brute", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mois: MOIS_CLE, liste, type: typeGarde }),
        });
        if (!saveRes.ok) {
          const t = await saveRes.text().catch(() => "");
          throw new Error(`Erreur serveur ${saveRes.status}. ${t}`);
        }
        setHasListeGarde(true);
        navigate(withCurrentBack(`/responsable/gardes-brute?mois=${encodeURIComponent(MOIS_CLE)}&type=${encodeURIComponent(typeGarde)}`));
      } catch (err) {
        console.error("❌ Erreur sauvegarde liste brute :", err);
        notify(`Erreur lors de l'enregistrement.\n${err.message || ""}`);
      }
    },
    [MOIS_CLE, typeGarde, navigate, pr2PrepData, getPr2AffectationsLesPlusRecentes]
  );

  // 🔀 Helper : charger les listes externes et avertir si des listes manquent
  function countJoursListe(obj) {
    if (!obj || typeof obj !== "object") return 0;
    return Object.keys(obj).filter((k) => !k.startsWith("_")).length;
  }

  async function loadExternalListsWithWarning(moisCle) {
    let externalLists = null;
    try {
      const ccRes = await apiFetch(`/api/v2/cross-check-gardes/${moisCle}`, {
        credentials: "include",
      });
      if (!ccRes.ok) {
        const t = await ccRes.text().catch(() => "");
        notify(
          `Impossible de charger les listes externes pour ${moisCle} (erreur serveur ${ccRes.status}).\n` +
            `La génération PR2 est bloquée : il faut les cinq secteurs (PR1, digestif, réanimation, thoracique, orthopédie).\n${t ? `\n${t.slice(0, 200)}` : ""}`
        );
        return null;
      }
      const {
        pr1,
        digestif,
        reanimation,
        thoracique,
        orthopedie,
        pr2PrevWeek,
        previousExternalLists,
        __meta: crossCheckMeta,
      } = await ccRes.json();
      externalLists = {
        pr1Liste: pr1,
        digestifListe: digestif,
        reanimationListe: reanimation,
        thoraciqueListe: thoracique,
        orthopedieListe: orthopedie,
        pr2PrevWeek,
        previousExternalLists,
        previousExternalMeta: crossCheckMeta,
      };

      const missing = [];
      const found = [];
      const need = [
        { label: "Garde PR1", obj: pr1 },
        { label: "Garde Digestif", obj: digestif },
        { label: "Garde Réanimation", obj: reanimation },
        { label: "Astreinte Thoracique", obj: thoracique },
        { label: "Astreinte Orthopédie", obj: orthopedie },
      ];
      for (const { label, obj } of need) {
        const n = countJoursListe(obj);
        if (n > 0) found.push(`✅ ${label} (${n} jour(s))`);
        else missing.push(`❌ ${label} (liste absente ou vide)`);
      }
      if (pr2PrevWeek && countJoursListe(pr2PrevWeek) > 0) {
        found.push(`✅ PR2 mois précédent (${countJoursListe(pr2PrevWeek)} jour(s))`);
      }
      const previousCounts = [
        ["PR1", previousExternalLists?.pr1],
        ["Digestif", previousExternalLists?.digestif],
        ["Réanimation", previousExternalLists?.reanimation],
        ["Thoracique", previousExternalLists?.thoracique],
        ["Orthopédie", previousExternalLists?.orthopedie],
      ]
        .map(([label, obj]) => `${label} ${countJoursListe(obj)}`)
        .join(" / ");
      if (previousExternalLists) {
        found.push(`✅ Bordure mois précédent (${crossCheckMeta?.previousMonth || "M-1"}) : ${previousCounts}`);
      }

      console.log(`🔀 Listes externes — ${found.join(", ")}${missing.length > 0 ? ` | Manquantes : ${missing.join(", ")}` : ""}`);

      if (missing.length > 0) {
        notify(
          `Génération PR2 bloquée — listes manquantes ou vides pour ${moisCle} :\n\n` +
            missing.join("\n") +
            "\n\nRenseignez les cinq secteurs (PR1, digestif, réanimation, thoracique, orthopédie) pour le mois avant de lancer l'algorithme.\n\n" +
            `Disponible aujourd'hui :\n${found.length > 0 ? found.join("\n") : "(rien)"}`
        );
        return null;
      }
    } catch (err) {
      console.warn("⚠️ Impossible de charger les listes externes :", err);
      notify(
        `Impossible de charger les listes externes pour ${moisCle}.\n\n` +
          `${err?.message || err}\n\n` +
          "La génération PR2 est bloquée tant que les cinq listes (PR1, digestif, réanimation, thoracique, orthopédie) ne sont pas accessibles."
      );
      return null;
    }
    return externalLists;
  }

  async function loadExternalListsWithDiagnostics(moisCle) {
    const diagnostics = [];
    const emptyLists = {
      pr1Liste: null,
      digestifListe: null,
      reanimationListe: null,
      thoraciqueListe: null,
      orthopedieListe: null,
      pr2PrevWeek: null,
      previousExternalLists: null,
      previousExternalMeta: null,
    };
    const monthMatch = /^(\d{4})-(\d{2})$/.exec(String(moisCle || ""));
    const expectedDays = monthMatch
      ? Array.from(
          { length: new Date(Number(monthMatch[1]), Number(monthMatch[2]), 0).getDate() },
          (_, i) => `${monthMatch[1]}-${monthMatch[2]}-${String(i + 1).padStart(2, "0")}`
        )
      : [];

    const summarize = (key, label, obj) => {
      const dates = obj && typeof obj === "object"
        ? Object.keys(obj).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d) && !!obj[d]).sort()
        : [];
      if (dates.length === 0) {
        diagnostics.push({
          key,
          label,
          severity: "warning",
          count: 0,
          message: `${label} : liste absente ou vide`,
        });
        return;
      }
      const missingDates = expectedDays.filter((d) => !dates.includes(d));
      if (missingDates.length > 0) {
        diagnostics.push({
          key,
          label,
          severity: "warning",
          count: dates.length,
          missingDates,
          message: `${label} : ${dates.length}/${expectedDays.length} date(s) renseignée(s), ${missingDates.length} date(s) absente(s)`,
        });
        return;
      }
      diagnostics.push({
        key,
        label,
        severity: "ok",
        count: dates.length,
        message: `${label} : ${dates.length}/${expectedDays.length} date(s) renseignée(s)`,
      });
    };

    try {
      const ccRes = await apiFetch(`/api/v2/cross-check-gardes/${moisCle}`, {
        credentials: "include",
      });
      if (!ccRes.ok) {
        const t = await ccRes.text().catch(() => "");
        diagnostics.push({
          key: "cross-check",
          label: "Listes externes",
          severity: "error",
          count: 0,
          message: `Lecture impossible (HTTP ${ccRes.status})${t ? ` — ${t.slice(0, 180)}` : ""}`,
        });
        return { lists: emptyLists, diagnostics };
      }

      const {
        pr1,
        digestif,
        reanimation,
        thoracique,
        orthopedie,
        pr2PrevWeek,
        previousExternalLists,
        __meta: crossCheckMeta,
      } = await ccRes.json();
      const lists = {
        pr1Liste: pr1,
        digestifListe: digestif,
        reanimationListe: reanimation,
        thoraciqueListe: thoracique,
        orthopedieListe: orthopedie,
        pr2PrevWeek,
        previousExternalLists,
        previousExternalMeta: crossCheckMeta,
      };
      summarize("pr1", "Garde PR1", pr1);
      summarize("digestif", "Garde Digestif", digestif);
      summarize("reanimation", "Garde Réanimation", reanimation);
      summarize("thoracique", "Astreinte Thoracique", thoracique);
      summarize("orthopedie", "Astreinte Orthopédie", orthopedie);
      if (pr2PrevWeek && countJoursListe(pr2PrevWeek) > 0) {
        diagnostics.push({
          key: "pr2PrevWeek",
          label: "PR2 mois précédent",
          severity: "ok",
          count: countJoursListe(pr2PrevWeek),
          message: `PR2 mois précédent : ${countJoursListe(pr2PrevWeek)} date(s) prise(s) en compte`,
        });
      }
      if (previousExternalLists && typeof previousExternalLists === "object") {
        const previousCounts = [
          ["PR1", previousExternalLists.pr1],
          ["Digestif", previousExternalLists.digestif],
          ["Réanimation", previousExternalLists.reanimation],
          ["Thoracique", previousExternalLists.thoracique],
          ["Orthopédie", previousExternalLists.orthopedie],
        ].map(([label, obj]) => `${label} ${countJoursListe(obj)}`);
        diagnostics.push({
          key: "previousExternalLists",
          label: "Bordure mois précédent",
          severity: "ok",
          count: previousCounts.reduce((sum, item) => sum + Number(item.match(/\d+$/)?.[0] || 0), 0),
          message: `Bordure ${crossCheckMeta?.previousMonth || "mois précédent"} (${crossCheckMeta?.previousMonthBorderDays || 6} derniers jours) : ${previousCounts.join(" / ")}`,
        });
      }
      return { lists, diagnostics };
    } catch (err) {
      diagnostics.push({
        key: "cross-check",
        label: "Listes externes",
        severity: "error",
        count: 0,
        message: `Lecture impossible — ${err?.message || err}`,
      });
      return { lists: emptyLists, diagnostics };
    }
  }

  const daysInMonth = useMemo(() => {
    if (!Number.isFinite(YEAR) || !Number.isFinite(MONTH_NUM)) return 28;
    return new Date(YEAR, MONTH_NUM, 0).getDate();
  }, [YEAR, MONTH_NUM]);

  // ===============================
  // 🔹 Chargement config (multi-mois)
  // ===============================
  useEffect(() => {
    const loadCfg = async () => {
      try {
        setCfgLoading(true);
        // ✅ Endpoint de config selon le type
        let endpoint = "/api/v2/config/desiderata-pr2"; // PR2 par défaut
        if (typeGarde === "PR1") endpoint = "/api/v2/config/desiderata-pr1";
        else if (typeGarde === "ASTREINTE_THORACIQUE") endpoint = "/api/v2/config/desiderata-astreinte-thoracique";
        else if (typeGarde === "OPH") endpoint = "/api/v2/config/desiderata-oph";
        else if (typeGarde === "INTERNE1") endpoint = "/api/v2/config/desiderata-interne1";
        else if (typeGarde === "INTERNE2") endpoint = "/api/v2/config/desiderata-interne2";
        else if (typeGarde === "DIGESTIF") endpoint = "/api/v2/config/desiderata-digestif";
        else if (typeGarde === "REANIMATION") endpoint = "/api/v2/config/desiderata-reanimation";
        const res = await apiFetch(endpoint, { cache: "no-store", credentials: "include" });
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} lecture config. ${t}`);
        }
        const json = (await res.json()) || {};
        setCfg(json);
      } catch (e) {
        console.error("❌ Erreur chargement config desiderata :", e);
        setCfg(null);
      } finally {
        setCfgLoading(false);
      }
    };
    loadCfg();
  }, [typeGarde]);

  // Init mois sélectionné : mois principal = cfg.mois_cle, sinon premier mois ouvert
  useEffect(() => {
    if (selectedMoisCle) return;

    if (cfg?.mois_cle) {
      setSelectedMoisCle(String(cfg.mois_cle));
      return;
    }
    if (moisOuverts.length > 0) {
      setSelectedMoisCle(String(moisOuverts[0].mois_cle));
    }
  }, [cfg, moisOuverts, selectedMoisCle]);

  // ===============================
  // 🔹 Chargement liste médecins (selon le type)
  // ===============================
  useEffect(() => {
    apiFetch(medecinsApiUrl, { credentials: "include" })
      .then((res) => res.json())
      .then((d) => setMedecins(medecinsPayloadToArray(d)))
      .catch((err) =>
        console.error(`❌ Erreur chargement médecins (${typeLabel}) :`, err)
      );
  }, [medecinsApiUrl, typeLabel]);

  // ===============================
  // 🔹 Vérifier si une liste BRUTE existe déjà pour le mois sélectionné
  // (⚠️ Important : NE PAS appeler /api/v2/liste_gardes/:mois ici, qui est public (= validé))
  // ===============================
  useEffect(() => {
    const checkListe = async () => {
      if (!MOIS_CLE) return;
      try {
        setHasListeGarde(false);

        // ✅ Endpoint de vérification selon le type
        let bruteEndpoint = `/api/v2/liste_gardes_brute/${MOIS_CLE}?type=${encodeURIComponent(typeGarde)}`;
        if (typeGarde === "ASTREINTE_THORACIQUE") bruteEndpoint = `/api/v2/listes-gardes-astreinte-thoracique/brute/${MOIS_CLE}`;
        
        const res = await apiFetch(bruteEndpoint, {
          cache: "no-store",
          credentials: "include",
        });

        if (!res.ok) return;

        const json = await res.json();
        if (json && Object.keys(json).length > 0) {
          setHasListeGarde(true);
        }
      } catch (e) {
        console.error(
          `❌ Erreur lors de la vérification de la liste BRUTE (${typeGarde}) :`,
          e
        );
      }
    };
    checkListe();
  }, [MOIS_CLE]);

  // ===============================
  // 🔹 Chargement des désidérata du mois sélectionné + calcul manquants
  // ===============================
  useEffect(() => {
    const load = async () => {
      if (!MOIS_CLE) return;
      try {
        setLoading(true);
        setError(null);
        setData([]);
        setMissingMedecins([]);
        setCanManageDesiderata(false);

        // ✅ Endpoint des désidératas selon le type
        let endpoint = `/api/v2/desiderata-pr2/${MOIS_CLE}`; // PR2 par défaut
        if (typeGarde === "PR1") endpoint = `/api/v2/desiderata-pr1/${MOIS_CLE}`;
        else if (typeGarde === "ASTREINTE_THORACIQUE") endpoint = `/api/v2/desiderata-astreinte-thoracique/${MOIS_CLE}`;
        else if (typeGarde === "OPH") endpoint = `/api/v2/desiderata-oph/${MOIS_CLE}`;
        else if (typeGarde === "INTERNE1") endpoint = `/api/v2/desiderata-interne1/${MOIS_CLE}`;
        else if (typeGarde === "INTERNE2") endpoint = `/api/v2/desiderata-interne2/${MOIS_CLE}`;
        else if (typeGarde === "DIGESTIF") endpoint = `/api/v2/desiderata-digestif/${MOIS_CLE}`;
        else if (typeGarde === "REANIMATION") endpoint = `/api/v2/desiderata-reanimation/${MOIS_CLE}`;
        
        console.log(
          `🧩 Chargement des désidérata ${typeLabel} via ${endpoint}...`
        );
        const res = await apiFetch(endpoint, {
          cache: "no-store",
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} – ${res.statusText}`);
        }
        const json = await res.json();

        setCanManageDesiderata(res.headers.get('X-Desiderata-Management') === 'true');
        console.log(`✅ Désidérata ${typeGarde} chargés :`, json);

        const entries = (json || [])
          .filter((item) => item && item.nom && item.prenom)
          .map((item) => ({
            nom: item.nom,
            prenom: item.prenom,
            fullName: `${item.nom} ${item.prenom}`,
            nbGardes: typeof item.nbGardes === "number" ? item.nbGardes : parseInt(item.nbGardes, 10) || 0,
            souhaits: item.valeurs || {}, // { date: { code, label } }
          }))
          .sort((a, b) =>
            a.nom.localeCompare(b.nom, "fr", { sensitivity: "base" })
          );

        setData(entries);

        // Calcul des médecins manquants (médecins inscrits mais sans désidérata)
        // ⚠️ Ne PAS recalculer si la liste medecins n'est pas encore chargée
        if (medecins && medecins.length > 0) {
          const medecinsNorm = medecins
            .filter((m) => m && m.nom && m.prenom)
            .map((m) => ({
              key: normalizeKey(`${m.nom}_${m.prenom}`),
              label: `${m.nom} ${m.prenom}`,
            }));

          const fichiersList = entries.map((e) =>
            normalizeKey(`${e.nom}_${e.prenom}`)
          );

          const manquants = medecinsNorm
            .filter((m) => !fichiersList.includes(m.key))
            .map((m) => m.label);

          console.log("🔍 [MANQUANTS] medecins:", medecinsNorm.length, "desiderata:", fichiersList.length,
            "manquants:", manquants.length, manquants);

          setMissingMedecins(manquants);
        } else {
          console.log("🔍 [MANQUANTS] medecins pas encore chargés, calcul ignoré");
        }
      } catch (err) {
        console.error("❌ Erreur de chargement désidérata PR2 :", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [medecins, MOIS_CLE, typeGarde, typeLabel]);

  // ✅ Chargement des ajustements bonus/malus (astreinte thoracique uniquement)
  useEffect(() => {
    if (!isAstreinteThoracique) return;
    const loadAjust = async () => {
      try {
        const r = await apiFetch("/api/v2/ajustements-astreinte-thoracique", {
          credentials: "include",
        });
        if (r.ok) {
          const data = await r.json();
          setAjustements(data || {});
          setSavedAjustements(data || {}); // copie serveur
        }
      } catch (_e) {
        console.log("ℹ️ Pas d'ajustements bonus/malus");
      }
      setAjustementsLoaded(true);
    };
    loadAjust();
  }, [isAstreinteThoracique]);

  // 🎨 Couleurs pour la grille
  const colorFor = (v) => {
    switch (v) {
      case 1:
        return "#3b82f6"; // souhaite absolument
      case 2:
        return "#22c55e"; // disponible
      case 3:
        return "#facc15"; // peu dispo
      case 0:
        return "#ef4444"; // indisponible
      default:
        return "#ffffff";
    }
  };

  const thStyle = {
    padding: "6px",
    border: "1px solid #e5e7eb",
    textAlign: "center",
    fontWeight: 600,
    fontSize: 12,
    whiteSpace: "nowrap",
  };
  const tdStyle = {
    padding: "6px",
    border: "1px solid #f1f5f9",
    textAlign: "center",
    fontWeight: 500,
  };
  const tdNomStyle = {
    ...tdStyle,
    fontWeight: 700,
    background: "#f9fafb",
    position: "sticky",
    left: 0,
    zIndex: 1,
  };

  const btnMain = {
    padding: "18px 20px",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: 700,
    border: "1px solid #111827",
    fontSize: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    background: "#111827",
    color: "#fff",
  };

  const btnSecondary = {
    ...btnMain,
    background: "#fff",
    color: "#111827",
    border: "1px solid #d1d5db",
  };

  const btnSelect = {
    padding: "10px 14px",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: 800,
    border: "1px solid #111827",
    fontSize: 14,
    background: "#111827",
    color: "#fff",
    outline: "none",
  };

  const runEquilibrage = async (selection = generationChoices.applied, applySelection = false) => {
              if (!generationChoices.ready || equilibrageBusy || nbGardesSavingKey !== null) return;
              const run = ++equilibrageRun.current;
              setEquilibrageBusy(true);
              try {
                const warnings = [];
                let desiderata = [];
                try {
                  const res = await apiFetch(`/api/v2/desiderata-pr2/${MOIS_CLE}`, { credentials: "include" });
                  if (!res.ok) throw new Error(`HTTP ${res.status}`);
                  desiderata = await res.json();
                  if (!Array.isArray(desiderata)) throw new Error("Réponse non exploitable");
                } catch (err) {
                  warnings.push(`Désidérata non lus (${err.message}) : aucun médecin ne pourra être attribué.`);
                  desiderata = [];
                }
                const submitted = new Set(desiderata.map((d) => normalizeKey(`${d.nom}_${d.prenom}`)));
                const missing = medecins.filter((m) => m.nom && m.prenom && !submitted.has(normalizeKey(`${m.nom}_${m.prenom}`)));
                if (missing.length) warnings.push(`Sans désidérata, exclus : ${missing.map((m) => `${m.nom} ${m.prenom}`).join(", ")}.`);
                const { lists, diagnostics } = await loadExternalListsWithDiagnostics(MOIS_CLE);
                warnings.push(...diagnostics.filter((d) => d.severity !== "ok").map((d) => d.message));
                const borderSources = [["PR2", lists.pr2PrevWeek], ...[
                  ["PR1", "pr1"], ["Digestif", "digestif"], ["Réanimation", "reanimation"],
                  ["Thoracique", "thoracique"], ["Orthopédie", "orthopedie"],
                ].map(([label, key]) => [label, lists.previousExternalLists?.[key]])];
                for (const [label, list] of borderSources) {
                  if (!list || !Object.entries(list).some(([d, m]) => /^\d{4}-\d{2}-\d{2}$/.test(d) && m))
                    warnings.push(`${label} : bordure du mois précédent absente ou vide.`);
                }
                if (run !== equilibrageRun.current) return;
                if (warnings.length && !(await confirmDialog(warnings.join("\n") +
                  "\n\nLes listes manquantes ne seront pas croisées avec la PR2 générée. Des conflits de même jour ou d’écart insuffisant peuvent rester indétectés. Relancez la vérification lorsque ces listes seront disponibles.\n\nContinuer avec les données disponibles ?"))) return;
                if (run !== equilibrageRun.current) return;
                const prep = preparerDonneesPR2(desiderata, YEAR, MONTH_NUM, { zeroGardesExcludes: true });
                const result = generationPr2EquilibragePermanence(prep, lists, { responsibleExclusions: selection });
                if (applySelection) generationChoices.apply(selection);
                setPr2PrepData(prep);
                setPr2ExternalLists(lists);
                setPr2ExternalDiagnostics(diagnostics);
                setPr2Phase1Result(null);
                setPr2Phase2Result({ ...result, avertissements: warnings });
                setPr2CrossCheck(null);
                setPr2Phase(2);
              } catch (err) {
                notify(`Calcul non terminé : ${err.message}. Aucune liste enregistrée.`);
              } finally { setEquilibrageBusy(false); }
            };
  const equilibrageButton = isPr2Secteur && Number.isInteger(YEAR) && MONTH_NUM >= 1 && MONTH_NUM <= 12 && <button type="button"
            disabled={nbGardesSavingKey !== null || equilibrageBusy || !generationChoices.ready}
            style={{ ...btnSecondary, background: "#166534", color: "#fff" }} onClick={() => runEquilibrage()}>
            {equilibrageBusy ? "Calcul en cours…" : "PR2 : Équilibrage de la permanence des soins"}
          </button>;

  const equilibragePanel = isPr2Secteur && pr2Phase2Result?.algorithme === "equilibrage_permanence" && pr2PrepData && (
        <Pr2EquilibrageResult result={pr2Phase2Result} jours={pr2PrepData.jours}
          warnings={pr2Phase2Result.avertissements || []}
          choices={generationChoices.draft} onChoiceChange={generationChoices.change}
          pending={generationChoices.pending} busy={equilibrageBusy}
          persistenceError={generationChoices.error}
          onRelaunch={() => runEquilibrage(generationChoices.draft, true)}
          onRestart={invalidatePr2Generation}
          onSave={() => enregistrerPr2BruteEtOuvrir({ titrePhase: "équilibrage de la permanence des soins" })} />
      );

  const isLoading = cfgLoading || loading;

  // ===============================
  // 🔧 Helpers algorithme → déplacés dans algo-gardes-pr2.js
  // ===============================

  if (error) {
    return (
      <div
        style={{
          maxWidth: "1200px",
          margin: "40px auto",
          padding: "24px",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <button
          style={{
            marginBottom: 20,
            padding: "10px 18px",
            borderRadius: 10,
            border: "1px solid #d1d5db",
            background: "#ffffff",
            cursor: "pointer",
            fontWeight: 600,
          }}
          onClick={() => navigate(gardesHubPath())}
        >
          {isAstreinteThoracique ? "← Retour à la gestion des astreintes thoraciques" : "← Retour à la gestion des gardes"}
        </button>

        <h1 style={{ fontSize: "28px", fontWeight: 800, marginBottom: 8 }}>
          {isAstreinteThoracique
            ? `✍️ Astreinte thoracique – désidérata ${MOIS_LABEL ? `(${MOIS_LABEL})` : ""}`
            : `✍️ Gardes ${typeLabel} – désidérata ${MOIS_LABEL ? `(${MOIS_LABEL})` : ""}`}
        </h1>
        <p style={{ color: "#374151", marginBottom: 24 }}>
          Erreur de chargement : {error}
        </p>
        {equilibrageButton}
        {equilibragePanel}
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "40px auto",
        padding: "24px",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <button
          style={{
            padding: "10px 18px",
            borderRadius: 10,
            border: "1px solid #d1d5db",
            background: "#ffffff",
            cursor: "pointer",
            fontWeight: 600,
          }}
          onClick={() => navigate(gardesHubPath())}
        >
          {isAstreinteThoracique ? "← Retour à la gestion des astreintes thoraciques" : "← Retour à la gestion des gardes"}
        </button>
        <LogoutButton />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 8,
        }}
      >
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: 800, marginBottom: 8 }}>
            {isAstreinteThoracique
              ? `✍️ Astreinte thoracique – désidérata ${MOIS_LABEL ? `(${MOIS_LABEL})` : ""}`
              : `✍️ Gardes ${typeLabel} – désidérata ${MOIS_LABEL ? `(${MOIS_LABEL})` : ""}`}
          </h1>
          <p style={{ color: "#374151", marginBottom: 0 }}>
            {isAstreinteThoracique
              ? "Consultation et validation des desiderata transmis par les médecins (astreinte thoracique)."
              : `Consultation et validation des desiderata transmis par les médecins de ${typeLabel}.`}
          </p>
        </div>

        {moisOuverts.length > 1 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              value={selectedMoisCle}
              onChange={(e) => {
                if (pr2Phase2Result?.algorithme === "equilibrage_permanence") invalidatePr2Generation();
                setSelectedMoisCle(e.target.value);
              }}
              style={btnSelect}
              title="Sélection du mois"
              disabled={cfgLoading}
            >
              {moisOuverts.map((m) => (
                <option
                  key={String(m.mois_cle)}
                  value={String(m.mois_cle)}
                  style={{ color: "#111827" }}
                >
                  {m.mois_label} ({m.mois_cle})
                </option>
              ))}
            </select>
            <span style={{ color: "#6b7280", fontSize: 13 }}>Mois</span>
          </div>
        ) : null}
      </div>

      {/* PR2 : rappel sous le titre (visible en premier au chargement ; ancre #rappel-pr2-doc) */}
      {!isLoading && isPr2Secteur && (
        <details
          id="rappel-pr2-doc"
          key={pr2Phase === 0 ? "pr2-doc-ouvert" : "pr2-doc-replie"}
          defaultOpen={pr2Phase === 0}
          style={{
            marginBottom: 16,
            borderRadius: 10,
            border: "2px solid #2563eb",
            background: "linear-gradient(180deg, #dbeafe 0%, #f1f5f9 100%)",
            scrollMarginTop: 72,
          }}
        >
          <summary
            style={{
              padding: "12px 16px",
              cursor: "pointer",
              fontWeight: 800,
              fontSize: 15,
              color: "#1e3a8a",
              listStyle: "none",
            }}
          >
            📌 Rappel PR2 — modalités, écarts et conflits (afficher / masquer)
          </summary>
          <div
            style={{
              padding: "4px 18px 16px",
              fontSize: 13,
              color: "#1e293b",
              lineHeight: 1.55,
              borderTop: "1px solid #93c5fd",
            }}
          >
            <p style={{ margin: "0 0 10px", fontWeight: 600, color: "#0f172a" }}>
              Deux voies de génération (boutons sous le tableau, en bas de page) :
            </p>
            <ul style={{ margin: "0 0 12px", paddingLeft: 20 }}>
              <li style={{ marginBottom: 6 }}>
                <strong>Générer la liste (Classique)</strong> — Phase 1 : une première PR2 par médecin éligible ; puis phases suivantes pour compléter (dont 2<sup>e</sup> garde PR2 selon les règles classiques).
              </li>
              <li style={{ marginBottom: 0 }}>
                <strong>PR2 — Engagement progressif</strong> — une garde chacun d’abord, puis une deuxième, puis les suivantes ; les gardes testent les écarts 6→5→4→3, les astreintes bloquent seulement J-1/J/J+1.
              </li>
            </ul>
            <p style={{ margin: "0 0 6px", fontWeight: 600, color: "#0f172a" }}>
              Écart minimal recherché (espacement)
            </p>
            <p style={{ margin: "0 0 12px" }}>
              L’algorithme mesure l’écart en <strong>jours calendaires</strong>. Pour un même médecin, entre deux dates où il a une activité prise en compte — <strong>PR2</strong>, <strong>PR1</strong>, <strong>digestif</strong>, <strong>réanimation</strong>, <strong>astreinte thoracique</strong>, <strong>astreinte orthopédie</strong>, et <strong>autres PR2</strong> déjà posées — l’écart doit être au moins <strong>{MIN_ECART_GARDES_PR2}</strong> jours,. Le bouton <strong>PR2 — Engagement progressif</strong> distingue les gardes et les astreintes : les gardes suivent la relaxation 6→5→4→3, tandis qu’une astreinte interdit seulement une PR2 le J-1, le J et le J+1. Si les listes de fin de mois précédent sont fournies, le même type de contrainte s’applique aussi entre ces dates et le mois en cours.
            </p>
            <p style={{ margin: "0 0 6px", fontWeight: 600, color: "#0f172a" }}>
              Pondération par jour (points / cible d’équilibrage)
            </p>
            <p style={{ margin: "0 0 12px" }}>
              Coefficient par date : <strong>1</strong> lun–ven hors férié, <strong>1,5</strong> samedi, <strong>2</strong> dimanche ou jour férié (y compris férié en semaine). <strong>Garde</strong> (PR1, digestif, réa, PR2) : <strong>1 ×</strong> coefficient ; <strong>astreinte</strong> (Tho, Ort) : <strong>0,5 ×</strong> coefficient. Ces totaux servent aux cibles d’équilibrage et aux charges externes affichées.
            </p>
            <p style={{ margin: "0 0 6px", fontWeight: 600, color: "#0f172a" }}>
              Conflit (après génération — contrôle « croisement »)
            </p>
            <p style={{ margin: 0 }}>
              Un <strong>conflit</strong> signalé dans les bandeaux de vérification correspond à l’une des situations suivantes pour un même médecin : <strong>même jour</strong> — une PR2 et une activité sur une autre liste (PR1, digestif, réa, Tho, Ort) tombent le même jour calendaire ; ou <strong>espacement insuffisant</strong> — moins de <strong>{MIN_ECART_GARDES_PR2}</strong> jours de grille entre une date PR2 et une autre activité comptée (y compris une autre PR2). Pour le générateur Engagement progressif, le contrôle final signale séparément les conflits de garde et les conflits d’astreinte J-1/J/J+1.
            </p>
          </div>
        </details>
      )}

      {/* ✅ STATISTIQUES */}
      {!isLoading && (
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <h3 style={{ fontSize: "16px", fontWeight: 700, marginBottom: "12px", color: "#1e293b" }}>
            📊 Statistiques
          </h3>
          <div
            style={{
              display: "flex",
              gap: "16px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {(() => {
              // Calcul des statistiques
              const totalMedecins = medecins.length;
              const medecinsSoumis = data.length;
              const medecinsAvecSouhaiteAbsolument = data.filter((med) => {
                const souhaits = med.souhaits || {};
                return Object.values(souhaits).some((v) => {
                  const code = typeof v === "object" && v !== null ? v.code : v;
                  return code === 1;
                });
              }).length;
              const medecinsAvecDisponible = data.filter((med) => {
                const souhaits = med.souhaits || {};
                return Object.values(souhaits).some((v) => {
                  const code = typeof v === "object" && v !== null ? v.code : v;
                  return code === 2;
                });
              }).length;
              const medecinsEligiblesMin1Garde = data.filter((med) => {
                const souhaits = med.souhaits || {};
                return Object.values(souhaits).some((v) => {
                  const code = typeof v === "object" && v !== null ? v.code : v;
                  return code === 1 || code === 2;
                });
              }).length;

              return (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      background: "#fff",
                      borderRadius: "6px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#475569" }}>Total médecins:</span>
                    <span style={{ fontWeight: 700, color: "#1e293b" }}>{totalMedecins}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      background: "#fff",
                      borderRadius: "6px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#475569" }}>Soumis:</span>
                    <span style={{ fontWeight: 700, color: "#1e293b" }}>{medecinsSoumis}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      background: "#fff",
                      borderRadius: "6px",
                      border: "1px solid #3b82f6",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#475569" }}>Souhaite absolument:</span>
                    <span style={{ fontWeight: 700, color: "#3b82f6" }}>{medecinsAvecSouhaiteAbsolument}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      background: "#fff",
                      borderRadius: "6px",
                      border: "1px solid #22c55e",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#475569" }}>Disponible:</span>
                    <span style={{ fontWeight: 700, color: "#22c55e" }}>{medecinsAvecDisponible}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      background: "#dbeafe",
                      borderRadius: "6px",
                      border: "1px solid #3b82f6",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#1e40af" }}>Éligibles min 1 garde:</span>
                    <span style={{ fontWeight: 700, color: "#1e40af" }}>{medecinsEligiblesMin1Garde}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* ✅ AFFICHAGE DES MANQUANTS POUR TOUS LES MOIS (même si liste déjà existante) */}
      {!isLoading && (
        <>
          {missingMedecins.length > 0 ? (
            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #f59e0b",
                color: "#92400e",
                padding: "10px",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <strong>
                ⚠️ Les médecins suivants n'ont pas soumis leurs desiderata :
              </strong>
              <ul style={{ marginTop: "8px", marginLeft: "20px" }}>
                {missingMedecins.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div
              style={{
                background: "#dcfce7",
                border: "1px solid #16a34a",
                color: "#065f46",
                padding: "10px",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              ✅ Tous les médecins ont soumis leurs desiderata de garde.
            </div>
          )}
          {isPr2Secteur && pr2ExternalDiagnostics.length > 0 && (
            <div
              data-testid="pr2-external-diagnostics"
              style={{
                background: pr2ExternalDiagnostics.some((d) => d.severity === "error") ? "#fee2e2" : "#fff7ed",
                border: pr2ExternalDiagnostics.some((d) => d.severity === "error") ? "1px solid #ef4444" : "1px solid #f97316",
                color: pr2ExternalDiagnostics.some((d) => d.severity === "error") ? "#991b1b" : "#9a3412",
                padding: "10px",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <strong>⚠️ Diagnostic listes externes PR2 — génération non bloquante</strong>
              <ul style={{ marginTop: "8px", marginLeft: "20px" }}>
                {pr2ExternalDiagnostics.map((d) => (
                  <li key={`${d.key}-${d.message}`}>
                    {d.severity === "ok" ? "✅" : "⚠️"} {d.message}
                    {Array.isArray(d.missingDates) && d.missingDates.length > 0 ? (
                      <span style={{ fontSize: 12 }}>
                        {" "}({d.missingDates.slice(0, 8).join(", ")}{d.missingDates.length > 8 ? ", ..." : ""})
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <div
        style={{
          marginBottom: 16,
          fontSize: 14,
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <span>🟦 Souhaite absolument</span>
        <span>🟩 Disponible</span>
        <span>🟨 Peu disponible</span>
        <span>🟥 Indisponible</span>
      </div>

      <div
        style={{
          overflowX: "auto",
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          background: "#fff",
          minHeight: 200,
        }}
      >
        {isLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            ⏳ Chargement en cours...
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead style={{ background: "#f3f4f6" }}>
              <tr>
                <th style={thStyle}>Médecin</th>
                <th style={{ ...thStyle, minWidth: 30, textAlign: "center" }} title="Supprimer ce desiderata">🗑️</th>
                <th style={{ ...thStyle, minWidth: 96, textAlign: "center" }} title="Nombre de gardes souhaitées">Nb</th>
                {[...Array(daysInMonth).keys()].map((i) => (
                  <th key={i} style={thStyle}>
                    {i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((med) => (
                <tr key={`${med.nom}_${med.prenom}`}>
                  <td style={tdNomStyle}>{med.fullName}</td>
                  <td style={{ ...tdNomStyle, textAlign: "center", padding: "2px" }}>
                    {canManageDesiderata && (
                    <button
                      type="button"
                      title={`Supprimer les desiderata de ${med.fullName}`}
                      style={{
                        background: "transparent",
                        border: "1px solid #fca5a5",
                        borderRadius: "4px",
                        cursor: "pointer",
                        padding: "4px 6px",
                        fontSize: "12px",
                        color: "#dc2626",
                        transition: "all 0.2s",
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = "#fef2f2";
                        e.target.style.borderColor = "#ef4444";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.borderColor = "#fca5a5";
                      }}
                      onClick={async () => {
                        const confirmDelete = (await confirmDialog(
                          `Voulez-vous vraiment supprimer les desiderata de ${med.fullName} pour ${MOIS_LABEL} ?\n\nCette action est irréversible.`
                        ));
                        if (!confirmDelete) return;

                        try {
                          // Déterminer l'endpoint selon le type
                          let endpoint = `/api/v2/desiderata-pr2/${MOIS_CLE}`;
                          if (typeGarde === "PR1") endpoint = `/api/v2/desiderata-pr1/${MOIS_CLE}`;
                          else if (typeGarde === "ASTREINTE_THORACIQUE") endpoint = `/api/v2/desiderata-astreinte-thoracique/${MOIS_CLE}`;
                          else if (typeGarde === "OPH") endpoint = `/api/v2/desiderata-oph/${MOIS_CLE}`;
                          else if (typeGarde === "INTERNE1") endpoint = `/api/v2/desiderata-interne1/${MOIS_CLE}`;
                          else if (typeGarde === "INTERNE2") endpoint = `/api/v2/desiderata-interne2/${MOIS_CLE}`;
                          else if (typeGarde === "DIGESTIF") endpoint = `/api/v2/desiderata-digestif/${MOIS_CLE}`;
                          else if (typeGarde === "REANIMATION") endpoint = `/api/v2/desiderata-reanimation/${MOIS_CLE}`;

                          const res = await apiFetch(
                            `${endpoint}?nom=${encodeURIComponent(med.nom)}&prenom=${encodeURIComponent(med.prenom)}`,
                            { method: "DELETE", credentials: "include" }
                          );

                          if (!res.ok) {
                            const errData = await res.json().catch(() => ({}));
                            throw new Error(errData.error || `Erreur HTTP ${res.status}`);
                          }

                          notify(`✅ Desiderata de ${med.fullName} supprimé avec succès.`);
                          // Recharger les données
                          window.location.reload();
                        } catch (err) {
                          console.error("❌ Erreur suppression desiderata:", err);
                          notify(`❌ Erreur lors de la suppression:\n${err.message}`);
                        }
                      }}
                    >
                      ✕
                    </button>
                    )}
                  </td>
                  <td style={{ ...tdNomStyle, textAlign: "center", fontWeight: 600, color: "#4b5563", padding: "4px 6px" }}>
                    {canManageDesiderata ? <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        aria-label={`Nombre de gardes souhaitées pour ${med.fullName}`}
                        disabled={isPr2Secteur && nbGardesSavingKey !== null}
                        value={Number.isFinite(Number(med.nbGardes)) ? med.nbGardes : 0}
                        onChange={(e) => {
                          const v = e.target.value;
                          invalidatePr2Generation();
                          setData((prev) =>
                            prev.map((m) =>
                              m.nom === med.nom && m.prenom === med.prenom
                                ? { ...m, nbGardes: v === "" ? 0 : Number(v) }
                                : m
                            )
                          );
                        }}
                        onBlur={() => saveNbGardesSouhaite(med)}
                        style={{
                          width: 48,
                          textAlign: "center",
                          border: "1px solid #cbd5e1",
                          borderRadius: 4,
                          padding: "2px 4px",
                          fontSize: 12,
                          color: "#1f2937",
                        }}
                      />
                      <button
                        type="button"
                        title="Sauvegarder Nb gardes souhaitées"
                        onClick={() => saveNbGardesSouhaite(med)}
                        disabled={nbGardesSavingKey === `${med.nom}_${med.prenom}`}
                        style={{
                          border: "1px solid #93c5fd",
                          background: nbGardesSavingKey === `${med.nom}_${med.prenom}` ? "#e5e7eb" : "#eff6ff",
                          color: "#1d4ed8",
                          borderRadius: 4,
                          fontSize: 11,
                          padding: "2px 6px",
                          cursor: nbGardesSavingKey === `${med.nom}_${med.prenom}` ? "not-allowed" : "pointer",
                        }}
                      >
                        💾
                      </button>
                    </div> : <span>{med.nbGardes}</span>}
                  </td>
                  {[...Array(daysInMonth).keys()].map((i) => {
                    const date = `${YEAR}-${pad2(MONTH_NUM)}-${pad2(i + 1)}`;
                    const valCode = med.souhaits?.[date]?.code ?? "";
                    return (
                      <td
                        key={i}
                        style={{ ...tdStyle, background: colorFor(valCode) }}
                      >
                        {""}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {data.length === 0 ? (
                <tr>
                  <td
                    style={{ ...tdStyle, textAlign: "left" }}
                    colSpan={daysInMonth + 3}
                  >
                    Aucun désidérata enregistré pour ce mois pour l’instant.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
      </div>

      {/* ═══ INTERFACE BONUS/MALUS — Astreinte thoracique uniquement ═══ */}
      {isAstreinteThoracique && ajustementsLoaded && (() => {
        // Déterminer le statut d'enregistrement des ajustements
        const hasAnyAjustement = Object.values(ajustements).some((v) => v !== 0 && v !== undefined && v !== null);
        const ajustementsMatchSaved = JSON.stringify(
          Object.fromEntries(Object.entries(ajustements).filter(([, v]) => v !== 0))
        ) === JSON.stringify(
          Object.fromEntries(Object.entries(savedAjustements).filter(([, v]) => v !== 0))
        );
        const ajustStatusLabel = !hasAnyAjustement
          ? "Aucun ajustement configuré"
          : ajustementsMatchSaved
            ? "Ajustements enregistrés"
            : "Modifications non enregistrées";
        const ajustStatusColor = !hasAnyAjustement
          ? "#94a3b8"
          : ajustementsMatchSaved ? "#16a34a" : "#ea580c";
        const ajustStatusBg = !hasAnyAjustement
          ? "#f1f5f9"
          : ajustementsMatchSaved ? "#dcfce7" : "#fff7ed";
        const ajustStatusBorder = !hasAnyAjustement
          ? "#e2e8f0"
          : ajustementsMatchSaved ? "#86efac" : "#fdba74";

        return (
        <div style={{ marginTop: 24, padding: 16, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
            onClick={() => setShowAjustements(!showAjustements)}
          >
            <span style={{ fontSize: 16 }}>{showAjustements ? "▼" : "▶"}</span>
            <strong style={{ fontSize: 14 }}>Ajustements bonus/malus (rééquilibrage quadrimestre précédent)</strong>
            <span style={{
              fontSize: 11,
              padding: "2px 10px",
              borderRadius: 12,
              background: ajustStatusBg,
              color: ajustStatusColor,
              border: `1px solid ${ajustStatusBorder}`,
              fontWeight: 600,
              marginLeft: 8,
              whiteSpace: "nowrap",
            }}>
              {ajustStatusLabel}
            </span>
          </div>

          {showAjustements && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
                Positif (+) = le médecin recevra davantage d'astreintes (rattrapage).
                Négatif (−) = le médecin recevra moins d'astreintes (compensation).
                Laisser à 0 si aucun rééquilibrage n'est nécessaire.
              </p>

              <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: 500 }}>
                <thead>
                  <tr>
                    <th style={{ padding: 6, borderBottom: "2px solid #e2e8f0", textAlign: "left", fontSize: 13 }}>Médecin (intérieur)</th>
                    <th style={{ padding: 6, borderBottom: "2px solid #e2e8f0", textAlign: "center", fontSize: 13, width: 100 }}>Ajustement</th>
                  </tr>
                </thead>
                <tbody>
                  {medecins
                    .filter((m) => m.origine === "interieur")
                    .filter((m) => {
                      // Exclure Nguyen (règle 5)
                      const n = (m.nom || "").toLowerCase();
                      const p = (m.prenom || "").toLowerCase();
                      return !(n === "nguyen" && p.includes("lan"));
                    })
                    .map((m) => {
                      const key = `${m.nom}_${m.prenom}`;
                      return (
                        <tr key={key}>
                          <td style={{ padding: 6, borderBottom: "1px solid #f1f5f9", fontSize: 13 }}>
                            {m.nom} {m.prenom} <span style={{ color: "#94a3b8", fontSize: 11 }}>({m.quotite}%)</span>
                          </td>
                          <td style={{ padding: 6, borderBottom: "1px solid #f1f5f9", textAlign: "center" }}>
                            <input
                              type="number"
                              value={ajustements[key] || 0}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10) || 0;
                                setAjustements((prev) => ({ ...prev, [key]: val }));
                              }}
                              style={{
                                width: 60,
                                textAlign: "center",
                                padding: "4px 6px",
                                border: "1px solid #cbd5e1",
                                borderRadius: 4,
                                fontSize: 13,
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>

              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const r = await apiFetch("/api/v2/ajustements-astreinte-thoracique", {
                        method: "PUT",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(ajustements),
                      });
                      if (r.ok) {
                        setSavedAjustements({ ...ajustements }); // mettre à jour la copie serveur
                      } else {
                        notify("❌ Erreur lors de l'enregistrement.");
                      }
                    } catch (e) {
                      notify("❌ Erreur réseau: " + e.message);
                    }
                  }}
                  style={{
                    padding: "6px 16px",
                    background: ajustementsMatchSaved ? "#94a3b8" : "#3b82f6",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor: ajustementsMatchSaved ? "default" : "pointer",
                    fontSize: 13,
                    opacity: ajustementsMatchSaved ? 0.7 : 1,
                    transition: "all 0.2s",
                  }}
                  disabled={ajustementsMatchSaved}
                >
                  {ajustementsMatchSaved ? "Ajustements enregistrés" : "Enregistrer les ajustements"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const reset = {};
                    medecins
                      .filter((m) => m.origine === "interieur")
                      .forEach((m) => { reset[`${m.nom}_${m.prenom}`] = 0; });
                    setAjustements(reset);
                  }}
                  style={{
                    padding: "6px 16px",
                    background: "#f1f5f9",
                    color: "#475569",
                    border: "1px solid #e2e8f0",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  Réinitialiser à 0
                </button>
              </div>
            </div>
          )}
        </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════
          INTERFACE DE REVUE PAR PHASES — Astreinte Thoracique
          ═══════════════════════════════════════════════════════════ */}

      {/* ═══ PHASE 1 : Revue des blocs weekends/fériés ═══ */}
      {isAstreinteThoracique && thoPhase === 1 && thoPhase1Result && (
        <div style={{ marginTop: 24, padding: 20, background: "#eff6ff", borderRadius: 10, border: "2px solid #3b82f6" }}>
          <h3 style={{ margin: "0 0 8px", color: "#1e40af", fontSize: 16 }}>
            Phase 1 — Attribution des blocs weekends / fériés (Règles 3+4)
          </h3>
          <p style={{ fontSize: 13, color: "#475569", margin: "0 0 16px" }}>
            Vérifiez et modifiez si nécessaire l'affectation de chaque bloc. Seuls les médecins <strong>intérieurs</strong> disponibles sont proposés.
          </p>

          {/* Calcul dynamique du cumul par médecin en fonction des sélections */}
          {(() => {
            // Calculer le cumul progressif par médecin d'après les sélections
            const cumulParMedecin = {};
            const cumulProgressif = []; // pour chaque bloc idx, le cumul du médecin sélectionné APRÈS ce bloc
            thoPhase1Result.propositions.forEach((prop, idx) => {
              const selectedKey = thoPhase1Selections[idx];
              if (selectedKey) {
                cumulParMedecin[selectedKey] = (cumulParMedecin[selectedKey] || 0) + prop.bloc.jours.length;
              }
              cumulProgressif.push(selectedKey ? cumulParMedecin[selectedKey] : null);
            });

            return (<>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#dbeafe" }}>
                <th style={{ padding: 8, textAlign: "left", borderBottom: "2px solid #93c5fd" }}>Bloc</th>
                <th style={{ padding: 8, textAlign: "center", borderBottom: "2px solid #93c5fd" }}>Jours</th>
                <th style={{ padding: 8, textAlign: "center", borderBottom: "2px solid #93c5fd" }}>Durée</th>
                <th style={{ padding: 8, textAlign: "left", borderBottom: "2px solid #93c5fd", minWidth: 200 }}>Médecin attribué</th>
                <th style={{ padding: 8, textAlign: "center", borderBottom: "2px solid #93c5fd" }}>Cumul P1</th>
              </tr>
            </thead>
            <tbody>
              {thoPhase1Result.propositions.map((prop, idx) => {
                const joursDates = prop.bloc.jours;
                const debut = joursDates[0];
                const fin = joursDates[joursDates.length - 1];
                const joursLabel = joursDates.map(j => {
                  const d = new Date(j + "T00:00:00");
                  return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
                }).join(", ");

                const cumul = cumulProgressif[idx];
                const selectedKey = thoPhase1Selections[idx];
                const totalMed = selectedKey ? cumulParMedecin[selectedKey] : null;
                const isMaxCumul = totalMed !== null && totalMed === Math.max(...Object.values(cumulParMedecin));
                const isLastBloc = cumul !== null && cumul === totalMed;
                const isNonAttribue = !prop.choisiKey;

                return (<>
                  <tr key={idx} style={{ background: isNonAttribue ? "#fef2f2" : (idx % 2 === 0 ? "#f8fafc" : "#fff") }}>
                    <td style={{ padding: 8, borderBottom: "1px solid #e2e8f0", fontWeight: 600 }}>
                      {debut} → {fin}
                      <br />
                      <span style={{ fontSize: 11, color: "#64748b", fontWeight: 400 }}>
                        {prop.bloc.type === "weekend" ? "Weekend" : prop.bloc.type === "weekend_etendu" ? "Weekend + férié/pont" : "Férié"}
                      </span>
                      {isNonAttribue && (
                        <span style={{ display: "block", fontSize: 11, color: "#dc2626", fontWeight: 700, marginTop: 2 }}>
                          ⚠️ Non attribué par l'algorithme
                        </span>
                      )}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #e2e8f0", textAlign: "center", fontSize: 11, color: "#64748b" }}>
                      {joursLabel}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #e2e8f0", textAlign: "center", fontWeight: 600 }}>
                      {joursDates.length}j
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #e2e8f0" }}>
                      {isNonAttribue ? (
                        <select
                          value={thoPhase1Selections[idx] || ""}
                          onChange={(e) => {
                            setThoPhase1Selections(prev => ({ ...prev, [idx]: e.target.value }));
                          }}
                          style={{
                            width: "100%", padding: "6px 8px",
                            border: "2px solid #ef4444",
                            borderRadius: 6, fontSize: 13,
                            background: "#fff5f5",
                          }}
                        >
                          <option value="">— Choisir un médecin (attribution manuelle) —</option>
                          {prop.tousCandidats && (() => {
                            const dispos = prop.tousCandidats.filter(c => c.disponible);
                            const indispos = prop.tousCandidats.filter(c => !c.disponible);
                            return (<>
                              {dispos.length > 0 && <optgroup label="── Disponibles ──">
                                {dispos.map(c => (
                                  <option key={c.key} value={c.key}>
                                    {c.pref === "souhaite absolument" ? "⭐ " : ""}{c.nom} {c.prenom} ({c.quotite}%{c.origine === "exterieur" ? ", ext." : ""}) — {c.pref}
                                  </option>
                                ))}
                              </optgroup>}
                              {indispos.length > 0 && <optgroup label="── Indisponibles / Sans desiderata ──">
                                {indispos.map(c => (
                                  <option key={c.key} value={c.key}>
                                    ⚠️ {c.nom} {c.prenom} ({c.quotite}%{c.origine === "exterieur" ? ", ext." : ""}) — {c.pref}
                                  </option>
                                ))}
                              </optgroup>}
                            </>);
                          })()}
                        </select>
                      ) : (
                        <select
                          value={thoPhase1Selections[idx] || ""}
                          onChange={(e) => {
                            setThoPhase1Selections(prev => ({ ...prev, [idx]: e.target.value }));
                          }}
                          style={{
                            width: "100%", padding: "6px 8px",
                            border: "1px solid #93c5fd",
                            borderRadius: 6, fontSize: 13,
                            background: "#fff",
                          }}
                        >
                          {prop.candidats.map((c) => (
                            <option key={c.key} value={c.key}>
                              {c.score === 0 ? "⭐ " : ""}{c.nom} {c.prenom} ({c.quotite}%)
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td style={{
                      padding: 8, borderBottom: "1px solid #e2e8f0", textAlign: "center",
                      fontWeight: 600, fontSize: 13,
                      color: isNonAttribue ? "#dc2626" : (isLastBloc && isMaxCumul ? "#dc2626" : "#334155"),
                      background: isLastBloc && isMaxCumul ? "#fef2f2" : "transparent",
                    }}>
                      {cumul !== null ? `${cumul}j` : "—"}
                    </td>
                  </tr>
                </>
                );
              })}
            </tbody>
          </table>

          {/* ═══ PANNEAU DE DIAGNOSTIC POUR BLOCS NON ATTRIBUÉS (après le tableau) ═══ */}
          {(() => {
            const blocsNonAttribues = thoPhase1Result.propositions
              .map((prop, idx) => ({ prop, idx }))
              .filter(({ prop }) => !prop.choisiKey && prop.diagnostic && prop.diagnostic.propositions.length > 0);
            if (blocsNonAttribues.length === 0) return null;
            return (
              <div style={{ marginTop: 16 }}>
                <div style={{
                  padding: "12px 16px", background: "#fff7ed", border: "2px solid #f97316",
                  borderRadius: 10, marginBottom: 0,
                }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#9a3412", marginBottom: 12 }}>
                    ⚠️ {blocsNonAttribues.length} bloc(s) non attribué(s) — Propositions pour le responsable
                  </div>
                  {blocsNonAttribues.map(({ prop, idx }) => {
                    const joursDates = prop.bloc.jours;
                    const joursLabel = joursDates.map(j => {
                      const d = new Date(j + "T00:00:00");
                      return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
                    }).join(", ");
                    return (
                      <div key={idx} style={{
                        padding: 12, marginBottom: 10, background: "#fef2f2",
                        border: "1px solid #fca5a5", borderRadius: 8,
                      }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: "#dc2626", marginBottom: 8 }}>
                          🔴 Bloc #{idx + 1} — {joursLabel} ({joursDates.length}j, {prop.bloc.type === "weekend" ? "Weekend" : prop.bloc.type === "weekend_etendu" ? "Weekend + férié/pont" : "Férié"})
                        </div>

                        {/* Analyse : médecins bloqués par espacement */}
                        {prop.diagnostic.bloqueParEspacement.length > 0 && (
                          <div style={{ fontSize: 12, color: "#78350f", marginBottom: 6 }}>
                            <strong>Médecin(s) disponible(s) mais bloqué(s) par l'espacement :</strong>
                            {prop.diagnostic.bloqueParEspacement.map((b, bi) => (
                              <div key={bi} style={{ marginLeft: 12, marginTop: 2 }}>
                                • {b.nom} {b.prenom} — espacement = {b.espacement} (min requis = 3), conflit avec bloc #{b.blocsVoisins[0]?.blocNum} ({b.blocsVoisins[0]?.jours})
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Analyse : médecins indisponibles */}
                        {prop.diagnostic.indisponibles.length > 0 && (
                          <div style={{ fontSize: 12, color: "#78350f", marginBottom: 6 }}>
                            <strong>Médecin(s) indisponible(s) :</strong>
                            {prop.diagnostic.indisponibles.map((ind, ii) => (
                              <div key={ii} style={{ marginLeft: 12, marginTop: 2 }}>
                                • {ind.nom} {ind.prenom} — indisponible : {ind.joursIndispo.join(", ")}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Propositions de résolution */}
                        <div style={{ marginTop: 8, fontSize: 12 }}>
                          {prop.diagnostic.propositions.map((p, pi) => {
                            const colors = {
                              A: { bg: "#fef3c7", border: "#f59e0b", icon: "🤝" },
                              B: { bg: "#f3e8ff", border: "#8b5cf6", icon: "📝" },
                              C: { bg: "#dbeafe", border: "#3b82f6", icon: "🏥" },
                            };
                            const c = colors[p.type] || colors.B;
                            return (
                              <div key={pi} style={{
                                padding: "8px 10px", marginTop: 4, borderRadius: 6,
                                background: c.bg, border: `1px solid ${c.border}`,
                              }}>
                                <div style={{ fontWeight: 600, color: "#1e293b" }}>
                                  {c.icon} Option {p.type} : {p.label}
                                </div>
                                {p.type === "B" && p.medecins ? (
                                  <div style={{ color: "#475569", marginTop: 4 }}>
                                    {p.medecins.map((med, mi) => (
                                      <div key={mi} style={{ marginLeft: 8, marginTop: 2 }}>
                                        • <strong>{med.nom} {med.prenom}</strong> — indisponible : {med.joursIndispo.join(", ")}
                                      </div>
                                    ))}
                                  </div>
                                ) : p.type === "C" && p.medecins ? (
                                  <div style={{ color: "#475569", marginTop: 4 }}>
                                    {p.medecins.map((med, mi) => (
                                      <div key={mi} style={{ marginLeft: 8, marginTop: 2 }}>
                                        • <strong>{med.nom} {med.prenom}</strong> ({med.quotite}%) — {med.pref}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div style={{ color: "#475569", marginTop: 2 }}>
                                    {p.detail}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          </>
            );
          })()}

          {/* ═══ TABLEAU RÉCAPITULATIF RATIOS PAR MÉDECIN ═══ */}
          {(() => {
            // Calculer les compteurs réels à partir des sélections actuelles
            const statsMap = {};
            for (const m of thoPrepData.interieursEquite) {
              const key = medKey(m.nom, m.prenom);
              statsMap[key] = { nom: m.nom, prenom: m.prenom, quotite: m.quotite, jours: 0, blocs: 0, ajust: 0 };
            }
            // Comptabiliser les jours et blocs par médecin d'après les sélections
            thoPhase1Result.propositions.forEach((prop, idx) => {
              const selectedKey = thoPhase1Selections[idx];
              if (selectedKey && statsMap[selectedKey]) {
                statsMap[selectedKey].jours += prop.bloc.jours.length;
                statsMap[selectedKey].blocs += 1;
              }
            });
            // Récupérer les ajustements (depuis compteurs de p1 — les ajustements sont dans les candidats)
            // On recalcule via les ajustements stockés
            const ajustFromCandidats = {};
            thoPhase1Result.propositions.forEach(prop => {
              if (prop.candidats) {
                prop.candidats.forEach(c => {
                  if (c.ajust !== undefined) ajustFromCandidats[c.key] = c.ajust;
                });
              }
            });
            // Calculer le total des jours attribués et la quotité totale
            const totalJoursP1 = Object.values(statsMap).reduce((sum, s) => sum + s.jours, 0);
            const totalQuotite = Object.values(statsMap).reduce((sum, s) => sum + s.quotite, 0);
            // Total des jours en bloc (tous les blocs, attribués ou non)
            const totalJoursBlocs = thoPhase1Result.propositions.reduce((sum, prop) => sum + prop.bloc.jours.length, 0);

            // === Calcul du nombre d'astreintes attendues par médecin intérieur ===
            // 1. Total jours du quadrimestre
            const totalJoursQuad = thoPrepData.tousLesJours.length;
            // 2. Jours restants hors blocs
            const joursRestantsHorsBlocs = totalJoursQuad - totalJoursBlocs;
            // 3. Nombre de mois
            const nbMois = thoAllMoisCles.length;
            // 4. Jours dédiés aux extérieurs (1 par externe par mois, ayant donné desiderata)
            const nbExterieurs = thoPrepData.exterieurs.length;
            const joursExterieurs = nbExterieurs * nbMois;
            // 5. Jours dédiés à Nguyen (1 jeudi par mois)
            const joursNguyen = thoPrepData.nguyenMedKey ? nbMois : 0;
            // 6. Jours restants pour les intérieurs (hors ext + Nguyen)
            const joursRestantsInterieurs = joursRestantsHorsBlocs - joursExterieurs - joursNguyen;
            // 7. Total astreintes attendues pour les intérieurs
            const totalAstreintesInterieurs = totalJoursBlocs + joursRestantsInterieurs;

            const rows = Object.entries(statsMap).map(([key, s]) => {
              const ajInit = ajustFromCandidats[key] || 0;
              // Astreintes attendues = (quotité / totalQuotité) × total astreintes intérieurs
              const astreintesAttendues = totalQuotite > 0 ? (s.quotite / totalQuotite) * totalAstreintesInterieurs : 0;
              // Part équitable = (quotité / totalQuotité) * totalJoursP1 attribués
              const partEquitable = totalQuotite > 0 ? (s.quotite / totalQuotite) * totalJoursP1 : 0;
              // Écart P1 = surplus ou déficit de jours par rapport à la part équitable
              return { key, ...s, ajustInit: ajInit, astreintesAttendues };
            });
            rows.sort((a, b) => a.nom.localeCompare(b.nom, 'fr') || a.prenom.localeCompare(b.prenom, 'fr'));

            return (
              <div style={{ marginTop: 16, padding: 12, background: "#f0f9ff", borderRadius: 8, border: "1px solid #bae6fd" }}>
                <h4 style={{ margin: "0 0 10px", fontSize: 14, color: "#0c4a6e" }}>
                  📊 Phase 1 — Attribution weekends et jours fériés (blocs) — Médecins intérieurs
                </h4>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#e0f2fe" }}>
                      <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "2px solid #7dd3fc" }}>Médecin</th>
                      <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "2px solid #7dd3fc" }}>Quotité</th>
                      <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "2px solid #7dd3fc" }}>Blocs</th>
                      <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "2px solid #7dd3fc" }}>Jours P1</th>
                      <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "2px solid #7dd3fc" }}>Astr. attendues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const rowBg = i % 2 === 0 ? "#fff" : "#f8fafc";
                      return (
                        <tr key={r.key} style={{ background: rowBg }}>
                          <td style={{ padding: "6px 10px", borderBottom: "1px solid #e0f2fe" }}>
                            {r.nom} {r.prenom}
                          </td>
                          <td style={{ padding: "6px 10px", borderBottom: "1px solid #e0f2fe", textAlign: "center" }}>
                            {r.quotite}%
                          </td>
                          <td style={{ padding: "6px 10px", borderBottom: "1px solid #e0f2fe", textAlign: "center", fontWeight: 600 }}>
                            {r.blocs}
                          </td>
                          <td style={{ padding: "6px 10px", borderBottom: "1px solid #e0f2fe", textAlign: "center", fontWeight: 600 }}>
                            {r.jours}
                          </td>
                          <td style={{ padding: "6px 10px", borderBottom: "1px solid #e0f2fe", textAlign: "center", fontWeight: 600, color: "#1e40af" }}>
                            {r.astreintesAttendues.toFixed(1)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: 8, fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>
                  <em>
                    Calcul astreintes attendues : ({totalJoursBlocs} j. blocs + {joursRestantsInterieurs} j. restants int.) = <b>{totalAstreintesInterieurs} j.</b> pour {Object.keys(statsMap).length} intérieurs
                    &nbsp;|&nbsp; Hors blocs : {joursRestantsHorsBlocs} − {joursExterieurs} ext. ({nbExterieurs}×{nbMois}m) − {joursNguyen} Nguyen = {joursRestantsInterieurs}
                  </em>
                </div>
              </div>
            );
          })()}

          {/* Vérification : tous les blocs doivent être attribués */}
          {(() => {
            const blocsManquants = thoPhase1Result.propositions
              .map((prop, idx) => ({ idx, selected: thoPhase1Selections[idx] }))
              .filter(({ selected }) => !selected);
            const tousAttribues = blocsManquants.length === 0;

            const buildBlocsPackage = () => {
              const affectationsBlocs = {};
              const compteurs = {};
              const compteursParMois = {};
              for (const m of thoPrepData.interieurs.concat(thoPrepData.exterieurs)) {
                const key = medKey(m.nom, m.prenom);
                compteurs[key] = 0;
                compteursParMois[key] = {};
                for (const mc of thoAllMoisCles) compteursParMois[key][mc] = 0;
              }
              thoPhase1Result.propositions.forEach((prop, idx) => {
                const choisiKey = thoPhase1Selections[idx];
                if (!choisiKey) return;
                for (const j of prop.bloc.jours) {
                  affectationsBlocs[j] = choisiKey;
                  compteurs[choisiKey] = (compteurs[choisiKey] || 0) + 1;
                  const moisDuJour = j.substring(0, 7);
                  if (compteursParMois[choisiKey]?.[moisDuJour] !== undefined) {
                    compteursParMois[choisiKey][moisDuJour]++;
                  }
                }
              });
              return { affectationsBlocs, compteurs, compteursParMois };
            };

            return (
          <>
          {!tousAttribues && (
            <div style={{
              marginTop: 12, padding: "10px 16px", background: "#fef2f2",
              border: "2px solid #fca5a5", borderRadius: 8, textAlign: "center",
              fontSize: 13, color: "#991b1b", fontWeight: 600,
            }}>
              ⚠️ {blocsManquants.length} bloc(s) sans médecin attribué (n°{blocsManquants.map(b => b.idx + 1).join(", ")}).
              Vous devez attribuer tous les blocs avant de valider la Phase 1.
            </div>
          )}
          <div style={{ marginTop: 14, fontSize: 13, fontWeight: 600, color: "#334155", textAlign: "center" }}>
            Phase suivante (jours restants) ou génération de la liste (éventuellement incomplète)
          </div>
          <div style={{ marginTop: 12, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => {
                setThoPhase(0);
                setThoPhase1Result(null);
                setThoPrepData(null);
                setThoJoursRestantsPack(null);
              }}
              style={{ padding: "8px 20px", background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontSize: 14 }}
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={!tousAttribues}
              onClick={async () => {
                console.log("✅ PHASE 1 VALIDÉE — Phase 2 (jours restants)");
                const { affectationsBlocs, compteurs, compteursParMois } = buildBlocsPackage();
                thoPhase1Result.propositions.forEach((prop, idx) => {
                  const original = prop.choisiKey;
                  const modifie = thoPhase1Selections[idx];
                  if (original !== modifie) {
                    console.log(`  🔧 Bloc ${prop.bloc.jours[0]}→${prop.bloc.jours[prop.bloc.jours.length - 1]} : ${original} → ${modifie} (MODIFIÉ)`);
                  }
                });
                let ajustementsData = {};
                try {
                  const resAjust = await apiFetch("/api/v2/ajustements-astreinte-thoracique", {
                    credentials: "include",
                  });
                  if (resAjust.ok) ajustementsData = await resAjust.json();
                } catch (_e) { /* ignore */ }
                console.log("🚀 Lancement Phase 2 (jours restants + Nguyen intégrée)");
                const p3 = phase3_joursRestants({
                  prepData: thoPrepData,
                  affectationsValidees: affectationsBlocs,
                  compteurs: { ...compteurs },
                  compteursParMois: JSON.parse(JSON.stringify(compteursParMois)),
                  ajustements: ajustementsData,
                });
                for (const log of p3.logs) console.log(log);
                setThoPhase3Allocations(p3.toutesAffectations);
                setThoPhase3Stats({
                  totalAttribue: p3.totalAttribue,
                  totalNonAttribue: p3.totalNonAttribue,
                  total: thoPrepData.tousLesJours.length,
                });
                setThoPhase4Allocations(null);
                setThoPhase4NbSwaps(0);
                setThoJoursRestantsPack({ p3, ajustementsData });
                setThoPhase(12);
              }}
              style={{
                padding: "8px 24px",
                background: tousAttribues ? "#3b82f6" : "#cbd5e1",
                color: tousAttribues ? "#fff" : "#94a3b8",
                border: "none", borderRadius: 8,
                cursor: tousAttribues ? "pointer" : "not-allowed",
                fontSize: 14, fontWeight: 600,
              }}
            >
              {tousAttribues ? "→ Phase 2 (jours restants)" : "⛔ Attribution incomplète"}
            </button>
            <button
              type="button"
              disabled={!tousAttribues}
              onClick={async () => {
                const { affectationsBlocs } = buildBlocsPackage();
                const total = thoPrepData.tousLesJours.length;
                const nbAttrib = Object.keys(affectationsBlocs).length;
                const na = total - nbAttrib;
                if (!(await confirmDialog(
                  `Générer la liste de garde avec uniquement les jours des blocs (week-ends / fériés) ?\n\n` +
                  `${na} jour(s) du quadrimestre resteront sans astreinte dans ce fichier. Continuer ?`
                ))) return;
                for (const mc of thoAllMoisCles) {
                  const affectationsMois = {};
                  for (const [date, med] of Object.entries(affectationsBlocs)) {
                    if (date.startsWith(mc)) affectationsMois[date] = med;
                  }
                  const saveRes = await apiFetch("/api/v2/liste_gardes/enregistrer_brute", {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ mois: mc, liste: affectationsMois, type: "ASTREINTE_THORACIQUE" }),
                  });
                  if (!saveRes.ok) {
                    const t = await saveRes.text().catch(() => "");
                    notify(`Erreur sauvegarde ${mc}: ${saveRes.status}. ${t}`);
                    return;
                  }
                  console.log(`💾 Sauvegarde brute (blocs seuls) ${mc}: ${Object.keys(affectationsMois).length} jours`);
                }
                setThoPhase3Allocations(affectationsBlocs);
                setThoPhase3Stats({ totalAttribue: nbAttrib, totalNonAttribue: na, total });
                setThoPhase4Allocations(null);
                setThoPhase4NbSwaps(0);
                setThoJoursRestantsPack(null);
                setHasListeGarde(true);
                setThoPhase(2);
              }}
              style={{
                padding: "8px 24px",
                background: tousAttribues ? "#fff" : "#f1f5f9",
                color: tousAttribues ? "#047857" : "#94a3b8",
                border: tousAttribues ? "2px solid #10b981" : "2px solid #cbd5e1",
                borderRadius: 8,
                cursor: tousAttribues ? "pointer" : "not-allowed",
                fontSize: 14, fontWeight: 600,
              }}
            >
              📋 Générer la liste (blocs uniquement)
            </button>
          </div>
          </>
            );
          })()}
        </div>
      )}

      {/* ═══ ASTREINTE THO — Entre Phase 2 (jours restants) et Phase 3 (swaps) ═══ */}
      {isAstreinteThoracique && thoPhase === 12 && thoJoursRestantsPack && thoPhase3Stats && (
        <div style={{ marginTop: 24, padding: 20, background: "#eff6ff", borderRadius: 10, border: "2px solid #3b82f6" }}>
          <h3 style={{ margin: "0 0 10px", color: "#1e40af", fontSize: 17 }}>
            Phase 2 (jours restants) terminée
          </h3>
          <p style={{ margin: "0 0 8px", fontSize: 14, color: "#334155" }}>
            <strong>{thoPhase3Stats.totalAttribue}</strong> / {thoPhase3Stats.total} jours attribués
            {thoPhase3Stats.totalNonAttribue > 0 && (
              <span style={{ color: "#b45309", fontWeight: 600 }}>
                {" "}— {thoPhase3Stats.totalNonAttribue} jour(s) sans astreinte
              </span>
            )}
          </p>
          <p style={{ margin: "0 0 16px", fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>
            Passez à la <strong>Phase 3</strong> (rééquilibrage par échanges) ou <strong>générez la liste de garde</strong> telle quelle (avec trous éventuels).
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => {
                setThoPhase(1);
                setThoJoursRestantsPack(null);
                setThoPhase3Allocations(null);
                setThoPhase3Stats(null);
                setThoPhase4Allocations(null);
                setThoPhase4NbSwaps(0);
              }}
              style={{ padding: "8px 20px", background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontSize: 14 }}
            >
              ← Retour (modifier les blocs)
            </button>
            <button
              type="button"
              onClick={async () => {
                const { p3, ajustementsData } = thoJoursRestantsPack;
                try {
                  console.log("🔄 Lancement Phase 3 (rééquilibrage par swaps)");
                  const p4 = phase4_reequilibrage({
                    prepData: thoPrepData,
                    toutesAffectations: p3.toutesAffectations,
                    compteurs: { ...p3.compteurs },
                    compteursParMois: JSON.parse(JSON.stringify(p3.compteursParMois)),
                    ajustements: ajustementsData,
                  });
                  for (const log of p4.logs) console.log(log);
                  for (const mc of thoAllMoisCles) {
                    const affectationsMois = {};
                    for (const [date, med] of Object.entries(p4.affectations)) {
                      if (date.startsWith(mc)) affectationsMois[date] = med;
                    }
                    const saveRes = await apiFetch("/api/v2/liste_gardes/enregistrer_brute", {
                      method: "POST",
                      credentials: "include",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ mois: mc, liste: affectationsMois, type: "ASTREINTE_THORACIQUE" }),
                    });
                    if (!saveRes.ok) {
                      const t = await saveRes.text().catch(() => "");
                      notify(`Erreur sauvegarde ${mc}: ${saveRes.status}. ${t}`);
                      return;
                    }
                    console.log(`💾 Sauvegarde brute ${mc}: ${Object.keys(affectationsMois).length} jours`);
                  }
                  setThoPhase4Allocations(p4.affectations);
                  setThoPhase4NbSwaps(p4.nbSwaps);
                  setThoJoursRestantsPack(null);
                  setHasListeGarde(true);
                  setThoPhase(2);
                } catch (err) {
                  console.error("❌ Erreur Phase 3 (swaps) :", err);
                  notify("Erreur Phase 3 : " + (err.message || err));
                }
              }}
              style={{ padding: "8px 24px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}
            >
              🔄 Phase 3 — Rééquilibrage (swaps)
            </button>
            <button
              type="button"
              onClick={async () => {
                const { p3 } = thoJoursRestantsPack;
                const n = p3.totalNonAttribue;
                if (!(await confirmDialog(
                  `Générer la liste de garde sans Phase 3 (sans rééquilibrage) ?\n\n` +
                  (n > 0 ? `⚠️ ${n} jour(s) sans astreinte dans le fichier.` : "Tous les jours sont attribués.") +
                  "\n\nContinuer ?"
                ))) return;
                try {
                  for (const mc of thoAllMoisCles) {
                    const affectationsMois = {};
                    for (const [date, med] of Object.entries(p3.toutesAffectations)) {
                      if (date.startsWith(mc)) affectationsMois[date] = med;
                    }
                    const saveRes = await apiFetch("/api/v2/liste_gardes/enregistrer_brute", {
                      method: "POST",
                      credentials: "include",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ mois: mc, liste: affectationsMois, type: "ASTREINTE_THORACIQUE" }),
                    });
                    if (!saveRes.ok) {
                      const t = await saveRes.text().catch(() => "");
                      notify(`Erreur sauvegarde ${mc}: ${saveRes.status}. ${t}`);
                      return;
                    }
                    console.log(`💾 Sauvegarde brute (sans swaps) ${mc}: ${Object.keys(affectationsMois).length} jours`);
                  }
                  setThoPhase4Allocations(null);
                  setThoPhase4NbSwaps(0);
                  setThoJoursRestantsPack(null);
                  setHasListeGarde(true);
                  setThoPhase(2);
                } catch (err) {
                  console.error("❌ Erreur sauvegarde :", err);
                  notify("Erreur : " + (err.message || err));
                }
              }}
              style={{ padding: "8px 24px", background: "#fff", color: "#1d4ed8", border: "2px solid #3b82f6", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}
            >
              📋 Générer la liste (sans Phase 3)
            </button>
          </div>
        </div>
      )}

      {/* ═══ PHASE 2 : Récapitulatif complet avant liste brute ═══ */}
      {isAstreinteThoracique && thoPhase === 2 && thoPhase3Allocations && (
        <div style={{ marginTop: 24, padding: 20, background: "#f0fdf4", borderRadius: 10, border: "2px solid #22c55e" }}>
          <div ref={thoRecapRef} style={{ background: "#f0fdf4", padding: 8 }}>
          <div style={{ marginBottom: 12, paddingBottom: 8, borderBottom: "2px solid #166534" }}>
            <h3 style={{ margin: "0 0 4px", color: "#166534", fontSize: 16 }}>
              Astreinte Thoracique — Récapitulatif de la génération
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: "#374151" }}>
              <strong>Quadrimestre :</strong>{" "}
              {thoAllMoisCles.map(m => {
                const [y, mo] = m.split("-");
                const moisNoms = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
                return `${moisNoms[parseInt(mo, 10)]} ${y}`;
              }).join(", ")}
              {" "}&mdash;{" "}
              <strong>Généré le :</strong>{" "}
              {new Date().toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          <h3 style={{ margin: "0 0 8px", color: "#166534", fontSize: 16 }}>
            Récapitulatif — Liste d'astreintes générée
          </h3>
          {thoPhase3Stats && (
            <p style={{ fontSize: 14, color: "#166534", margin: "0 0 16px", fontWeight: 600 }}>
              {thoPhase3Stats.totalAttribue} jours attribués / {thoPhase3Stats.total} jours
              {thoPhase3Stats.totalNonAttribue > 0
                ? ` — ⚠️ ${thoPhase3Stats.totalNonAttribue} jour(s) non attribué(s)`
                : " — ✅ Tous les jours attribués"}
            </p>
          )}

          {/* ═══ RÉCAPITULATIF PHASE 1 (identique à l'écran Phase 1) ═══ */}
          {(() => {
            const statsMapP1 = {};
            for (const m of thoPrepData.interieursEquite) {
              const key = medKey(m.nom, m.prenom);
              statsMapP1[key] = { nom: m.nom, prenom: m.prenom, quotite: m.quotite, jours: 0, blocs: 0 };
            }
            if (thoPhase1Result && thoPhase1Result.propositions) {
              thoPhase1Result.propositions.forEach((prop, idx) => {
                const selectedKey = thoPhase1Selections[idx];
                if (selectedKey && statsMapP1[selectedKey]) {
                  statsMapP1[selectedKey].jours += prop.bloc.jours.length;
                  statsMapP1[selectedKey].blocs += 1;
                }
              });
            }
            const totalJoursP1 = Object.values(statsMapP1).reduce((sum, s) => sum + s.jours, 0);
            const totalQuotiteP1 = Object.values(statsMapP1).reduce((sum, s) => sum + s.quotite, 0);
            // Calcul Astr. attendues (identique à Phase 1)
            const totalJoursBlocsP2 = thoPhase1Result ? thoPhase1Result.propositions.reduce((sum, prop) => sum + prop.bloc.jours.length, 0) : 0;
            const totalJoursQuadP2 = thoPrepData.tousLesJours.length;
            const joursRestantsHorsBlocsP2 = totalJoursQuadP2 - totalJoursBlocsP2;
            const nbMoisP2 = thoAllMoisCles.length;
            const nbExterieursP2 = thoPrepData.exterieurs.length;
            const joursExterieursP2 = nbExterieursP2 * nbMoisP2;
            const joursNguyenP2 = thoPrepData.nguyenMedKey ? nbMoisP2 : 0;
            const joursRestantsInterieursP2 = joursRestantsHorsBlocsP2 - joursExterieursP2 - joursNguyenP2;
            const totalAstreintesInterieursP2 = totalJoursBlocsP2 + joursRestantsInterieursP2;

            const rowsP1 = Object.entries(statsMapP1).map(([key, s]) => {
              const astreintesAttendues = totalQuotiteP1 > 0 ? (s.quotite / totalQuotiteP1) * totalAstreintesInterieursP2 : 0;
              return { key, ...s, astreintesAttendues };
            });
            rowsP1.sort((a, b) => a.nom.localeCompare(b.nom, 'fr') || a.prenom.localeCompare(b.prenom, 'fr'));

            return (
              <div style={{ marginTop: 8, padding: 12, background: "#f0f9ff", borderRadius: 8, border: "1px solid #bae6fd" }}>
                <h4 style={{ margin: "0 0 10px", fontSize: 14, color: "#0c4a6e" }}>
                  📊 Phase 1 — Attribution weekends et jours fériés (blocs) — Médecins intérieurs
                </h4>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#e0f2fe" }}>
                      <th style={{ padding: "5px 8px", textAlign: "left", borderBottom: "2px solid #7dd3fc" }}>Médecin</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #7dd3fc" }}>Quotité</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #7dd3fc" }}>Blocs</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #7dd3fc" }}>Jours P1</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #7dd3fc" }}>Astr. attendues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsP1.map((r, i) => {
                      return (
                        <tr key={r.key} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #e0f2fe" }}>{r.nom} {r.prenom}</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #e0f2fe", textAlign: "center" }}>{r.quotite}%</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #e0f2fe", textAlign: "center", fontWeight: 600 }}>{r.blocs}</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #e0f2fe", textAlign: "center", fontWeight: 600 }}>{r.jours}</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #e0f2fe", textAlign: "center", fontWeight: 600, color: "#1e40af" }}>
                            {r.astreintesAttendues.toFixed(1)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: 8, fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>
                  <em>
                    Calcul astreintes attendues : ({totalJoursBlocsP2} j. blocs + {joursRestantsInterieursP2} j. restants int.) = <b>{totalAstreintesInterieursP2} j.</b> pour {Object.keys(statsMapP1).length} intérieurs
                    &nbsp;|&nbsp; Hors blocs : {joursRestantsHorsBlocsP2} − {joursExterieursP2} ext. ({nbExterieursP2}×{nbMoisP2}m) − {joursNguyenP2} Nguyen = {joursRestantsInterieursP2}
                  </em>
                </div>
              </div>
            );
          })()}

          {/* ═══ RÉCAPITULATIF FIN DE PHASE 2 — APRÈS TOUS LES JOURS ATTRIBUÉS ═══ */}
          {(() => {
            // Compter jours par médecin intérieur depuis les allocations complètes (P1+P2+P3)
            const allocs = thoPhase3Allocations || {};
            const phase2RecapTitre = thoPhase3Stats && thoPhase3Stats.totalNonAttribue > 0
              ? "📊 Récapitulatif Phase 2 — Attribution partielle (trous)"
              : "📊 Récapitulatif Phase 2 — Tous les jours attribués";
            const statsMap = {};
            for (const m of thoPrepData.interieursEquite) {
              const key = medKey(m.nom, m.prenom);
              statsMap[key] = { nom: m.nom, prenom: m.prenom, quotite: m.quotite, jours: 0 };
            }
            for (const [, medK] of Object.entries(allocs)) {
              if (medK && statsMap[medK]) {
                statsMap[medK].jours += 1;
              }
            }

            // Récupérer les ajustements du quadrimestre précédent
            const ajustFromCandidats = {};
            if (thoPhase1Result && thoPhase1Result.propositions) {
              thoPhase1Result.propositions.forEach(prop => {
                if (prop.candidats) prop.candidats.forEach(c => { if (c.ajust !== undefined) ajustFromCandidats[c.key] = c.ajust; });
              });
            }

            const totalQuotite = Object.values(statsMap).reduce((sum, s) => sum + s.quotite, 0);

            // Calcul Astr. base (proportionnelle à la quotité, sans ajustement)
            const totalJoursBlocsF = thoPhase1Result ? thoPhase1Result.propositions.reduce((sum, prop) => sum + prop.bloc.jours.length, 0) : 0;
            const totalJoursQuadF = thoPrepData.tousLesJours.length;
            const joursRestantsHorsBlocsF = totalJoursQuadF - totalJoursBlocsF;
            const nbMoisF = thoAllMoisCles.length;
            const nbExterieursF = thoPrepData.exterieurs.length;
            const joursExterieursF = nbExterieursF * nbMoisF;
            const joursNguyenF = thoPrepData.nguyenMedKey ? nbMoisF : 0;
            const joursRestantsInterieursF = joursRestantsHorsBlocsF - joursExterieursF - joursNguyenF;
            const totalAstreintesInterieursF = totalJoursBlocsF + joursRestantsInterieursF;

            const rows = Object.entries(statsMap).map(([key, s]) => {
              const ajustQuadPrec = ajustFromCandidats[key] || 0;
              const astrBase = totalQuotite > 0 ? (s.quotite / totalQuotite) * totalAstreintesInterieursF : 0;
              const astrAttendues = astrBase + ajustQuadPrec;
              const astrObtenues = s.jours;
              const ecartResiduel = Math.round(astrObtenues - astrAttendues);
              return { key, ...s, astrBase, ajustQuadPrec, astrAttendues, astrObtenues, ecartResiduel };
            });
            rows.sort((a, b) => a.nom.localeCompare(b.nom, 'fr') || a.prenom.localeCompare(b.prenom, 'fr'));

            return (
              <div style={{ marginTop: 12, padding: 12, background: "#fefce8", borderRadius: 8, border: "1px solid #fde68a" }}>
                <h4 style={{ margin: "0 0 10px", fontSize: 14, color: "#92400e" }}>
                  {phase2RecapTitre}
                </h4>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#fef9c3" }}>
                      <th style={{ padding: "5px 8px", textAlign: "left", borderBottom: "2px solid #fbbf24" }}>Médecin</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #fbbf24" }}>Quotité</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #fbbf24" }}>Astreintes</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #fbbf24" }}>Ajust. quad. préc.</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #fbbf24" }}>Astr. attendues</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #fbbf24" }}>Astr. obtenues</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #fbbf24" }}>Écart résiduel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const ecartColor = r.ecartResiduel > 0 ? "#dc2626" : r.ecartResiduel < 0 ? "#059669" : "#16a34a";
                      const ecartBg = r.ecartResiduel === 0 ? "#f0fdf4" : Math.abs(r.ecartResiduel) >= 3 ? "#fef2f2" : "transparent";
                      return (
                        <tr key={r.key} style={{ background: i % 2 === 0 ? "#fff" : "#fffbeb" }}>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fef9c3" }}>{r.nom} {r.prenom}</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fef9c3", textAlign: "center" }}>{r.quotite}%</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fef9c3", textAlign: "center", color: "#1e40af" }}>
                            {r.astrBase.toFixed(1)}
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fef9c3", textAlign: "center", fontWeight: 600, color: r.ajustQuadPrec > 0 ? "#dc2626" : r.ajustQuadPrec < 0 ? "#059669" : "#64748b" }}>
                            {r.ajustQuadPrec > 0 ? "+" : ""}{r.ajustQuadPrec}
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fef9c3", textAlign: "center", fontWeight: 600, color: "#7c3aed" }}>
                            {r.astrAttendues.toFixed(1)}
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fef9c3", textAlign: "center", fontWeight: 700, color: "#0f172a" }}>
                            {r.astrObtenues}
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fef9c3", textAlign: "center", fontWeight: 700, color: ecartColor, background: ecartBg }}>
                            {r.ecartResiduel > 0 ? "+" : ""}{r.ecartResiduel}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: 6, fontSize: 11, color: "#64748b", lineHeight: 1.4 }}>
                  <em>
                    Astreintes = part proportionnelle à la quotité ({totalAstreintesInterieursF} j. intérieurs).
                    Astr. attendues = Astreintes + Ajust. quad. préc.
                    Écart résiduel = Obtenues − Attendues → à reporter au quadrimestre suivant.
                  </em>
                </div>
              </div>
            );
          })()}

          {/* ═══ RÉCAPITULATIF PHASE 3 — APRÈS RÉÉQUILIBRAGE PAR SWAPS ═══ */}
          {(() => {
            if (!thoPhase4Allocations) return null;
            const allocs = thoPhase4Allocations;
            const statsMap = {};
            for (const m of thoPrepData.interieursEquite) {
              const key = medKey(m.nom, m.prenom);
              statsMap[key] = { nom: m.nom, prenom: m.prenom, quotite: m.quotite, jours: 0 };
            }
            for (const [, medK] of Object.entries(allocs)) {
              if (medK && statsMap[medK]) {
                statsMap[medK].jours += 1;
              }
            }

            // Récupérer les ajustements du quadrimestre précédent
            const ajustFromCandidats = {};
            if (thoPhase1Result && thoPhase1Result.propositions) {
              thoPhase1Result.propositions.forEach(prop => {
                if (prop.candidats) prop.candidats.forEach(c => { if (c.ajust !== undefined) ajustFromCandidats[c.key] = c.ajust; });
              });
            }

            const totalQuotite = Object.values(statsMap).reduce((sum, s) => sum + s.quotite, 0);

            // Calcul Astr. base (proportionnelle à la quotité, sans ajustement)
            const totalJoursBlocsR = thoPhase1Result ? thoPhase1Result.propositions.reduce((sum, prop) => sum + prop.bloc.jours.length, 0) : 0;
            const totalJoursQuadR = thoPrepData.tousLesJours.length;
            const joursRestantsHorsBlocsR = totalJoursQuadR - totalJoursBlocsR;
            const nbMoisR = thoAllMoisCles.length;
            const nbExterieursR = thoPrepData.exterieurs.length;
            const joursExterieursR = nbExterieursR * nbMoisR;
            const joursNguyenR = thoPrepData.nguyenMedKey ? nbMoisR : 0;
            const joursRestantsInterieursR = joursRestantsHorsBlocsR - joursExterieursR - joursNguyenR;
            const totalAstreintesInterieursR = totalJoursBlocsR + joursRestantsInterieursR;

            const rows = Object.entries(statsMap).map(([key, s]) => {
              const ajustQuadPrec = ajustFromCandidats[key] || 0;
              const astrBase = totalQuotite > 0 ? (s.quotite / totalQuotite) * totalAstreintesInterieursR : 0;
              const astrAttendues = astrBase + ajustQuadPrec;
              const astrObtenues = s.jours;
              const ecartResiduel = Math.round(astrObtenues - astrAttendues);
              return { key, ...s, astrBase, ajustQuadPrec, astrAttendues, astrObtenues, ecartResiduel };
            });
            rows.sort((a, b) => a.nom.localeCompare(b.nom, 'fr') || a.prenom.localeCompare(b.prenom, 'fr'));

            return (
              <div style={{ marginTop: 12, padding: 12, background: "#f0fdf4", borderRadius: 8, border: "1px solid #86efac" }}>
                <h4 style={{ margin: "0 0 10px", fontSize: 14, color: "#166534" }}>
                  📊 Récapitulatif Phase 3 — Après rééquilibrage ({thoPhase4NbSwaps} swap{thoPhase4NbSwaps > 1 ? "s" : ""})
                </h4>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#dcfce7" }}>
                      <th style={{ padding: "5px 8px", textAlign: "left", borderBottom: "2px solid #86efac" }}>Médecin</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #86efac" }}>Quotité</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #86efac" }}>Astreintes</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #86efac" }}>Ajust. quad. préc.</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #86efac" }}>Astr. attendues</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #86efac" }}>Astr. obtenues</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #86efac" }}>Écart résiduel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const ecartColor = r.ecartResiduel > 0 ? "#dc2626" : r.ecartResiduel < 0 ? "#059669" : "#16a34a";
                      const ecartBg = r.ecartResiduel === 0 ? "#f0fdf4" : Math.abs(r.ecartResiduel) >= 3 ? "#fef2f2" : "transparent";
                      return (
                        <tr key={r.key} style={{ background: i % 2 === 0 ? "#fff" : "#f0fdf4" }}>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #dcfce7" }}>{r.nom} {r.prenom}</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #dcfce7", textAlign: "center" }}>{r.quotite}%</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #dcfce7", textAlign: "center", color: "#1e40af" }}>
                            {r.astrBase.toFixed(1)}
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #dcfce7", textAlign: "center", fontWeight: 600, color: r.ajustQuadPrec > 0 ? "#dc2626" : r.ajustQuadPrec < 0 ? "#059669" : "#64748b" }}>
                            {r.ajustQuadPrec > 0 ? "+" : ""}{r.ajustQuadPrec}
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #dcfce7", textAlign: "center", fontWeight: 600, color: "#7c3aed" }}>
                            {r.astrAttendues.toFixed(1)}
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #dcfce7", textAlign: "center", fontWeight: 700, color: "#0f172a" }}>
                            {r.astrObtenues}
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #dcfce7", textAlign: "center", fontWeight: 700, color: ecartColor, background: ecartBg }}>
                            {r.ecartResiduel > 0 ? "+" : ""}{r.ecartResiduel}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: 6, fontSize: 11, color: "#64748b", lineHeight: 1.4 }}>
                  <em>
                    Astreintes = part proportionnelle à la quotité ({totalAstreintesInterieursR} j. intérieurs).
                    Astr. attendues = Astreintes + Ajust. quad. préc.
                    Écart résiduel = Obtenues − Attendues → à reporter au quadrimestre suivant.
                  </em>
                </div>
              </div>
            );
          })()}

          {/* ═══ TABLEAU DES ÉCARTS MINIMAUX ENTRE ASTREINTES ═══ */}
          {(() => {
            // Utiliser les allocations finales (après rééquilibrage si disponible, sinon après P2)
            const finalAllocs = thoPhase4Allocations || thoPhase3Allocations || {};
            const blocsData = thoPrepData.blocs || [];

            // Construire un mapping : date → index de bloc (pour identifier les jours consécutifs d'un même bloc)
            const dateToBlocIdx = {};
            blocsData.forEach((bloc, idx) => {
              for (const j of bloc.jours) {
                dateToBlocIdx[j] = idx;
              }
            });

            // Regrouper les jours par médecin (TOUS : intérieurs + extérieurs + Nguyen)
            const joursParMedecin = {};
            for (const [date, medK] of Object.entries(finalAllocs)) {
              if (!medK) continue;
              if (!joursParMedecin[medK]) joursParMedecin[medK] = [];
              joursParMedecin[medK].push(date);
            }

            // Calculer l'écart min pour chaque médecin
            const ecartRows = [];
            // Collecter tous les médecins (intérieurs + extérieurs + Nguyen)
            const tousMedecins = [
              ...thoPrepData.interieursEquite.map(m => ({ nom: m.nom, prenom: m.prenom, origine: "Intérieur" })),
              ...thoPrepData.exterieurs.map(m => ({ nom: m.nom, prenom: m.prenom, origine: "Extérieur" })),
            ];
            if (thoPrepData.nguyenMed) {
              tousMedecins.push({ nom: thoPrepData.nguyenMed.nom, prenom: thoPrepData.nguyenMed.prenom, origine: "Intérieur (Nguyen)" });
            }

            for (const med of tousMedecins) {
              const key = medKey(med.nom, med.prenom);
              const jours = joursParMedecin[key];
              if (!jours || jours.length === 0) continue;

              // Trier chronologiquement
              const joursTries = [...jours].sort();

              // Regrouper en « occurrences » : les jours consécutifs d'un même bloc comptent comme une seule occurrence
              // L'occurrence est représentée par le dernier jour du groupe (pour calculer l'écart avec le suivant)
              const occurrences = []; // chaque occurrence = { debut: "2026-05-01", fin: "2026-05-03", nbJours: 3, enBloc: true }
              let i = 0;
              while (i < joursTries.length) {
                const debut = joursTries[i];
                const blocIdx = dateToBlocIdx[debut];
                let fin = debut;
                let nbJours = 1;
                // Regrouper les jours consécutifs du même bloc
                if (blocIdx !== undefined) {
                  while (i + 1 < joursTries.length && dateToBlocIdx[joursTries[i + 1]] === blocIdx) {
                    i++;
                    fin = joursTries[i];
                    nbJours++;
                  }
                }
                occurrences.push({ debut, fin, nbJours, enBloc: blocIdx !== undefined });
                i++;
              }

              // Calculer les écarts entre occurrences successives
              let ecartMin = null;
              let ecartMax = null;
              let pairMin = null;
              for (let o = 1; o < occurrences.length; o++) {
                const prev = occurrences[o - 1];
                const curr = occurrences[o];
                // Écart = nb de jours entre la fin de l'occurrence précédente et le début de l'occurrence suivante
                const dateFin = new Date(prev.fin + "T00:00:00");
                const dateDebut = new Date(curr.debut + "T00:00:00");
                const ecartJours = Math.round((dateDebut - dateFin) / (1000 * 60 * 60 * 24));
                if (ecartMin === null || ecartJours < ecartMin) {
                  ecartMin = ecartJours;
                  pairMin = `${prev.fin} → ${curr.debut}`;
                }
                if (ecartMax === null || ecartJours > ecartMax) {
                  ecartMax = ecartJours;
                }
              }

              ecartRows.push({
                key, nom: med.nom, prenom: med.prenom, origine: med.origine,
                nbAstreintes: jours.length,
                nbOccurrences: occurrences.length,
                ecartMin, ecartMax, pairMin,
              });
            }

            // Trier par écart min croissant (les plus serrés en premier), puis alphabétique
            ecartRows.sort((a, b) => {
              if (a.ecartMin === null && b.ecartMin === null) return a.nom.localeCompare(b.nom, 'fr');
              if (a.ecartMin === null) return 1;
              if (b.ecartMin === null) return -1;
              if (a.ecartMin !== b.ecartMin) return a.ecartMin - b.ecartMin;
              return a.nom.localeCompare(b.nom, 'fr') || a.prenom.localeCompare(b.prenom, 'fr');
            });

            return (
              <div style={{ marginTop: 12, padding: 12, background: "#fdf2f8", borderRadius: 8, border: "1px solid #f9a8d4" }}>
                <h4 style={{ margin: "0 0 10px", fontSize: 14, color: "#9d174d" }}>
                  📏 Écarts entre astreintes — par médecin (hors jours consécutifs d'un même bloc)
                </h4>
                <p style={{ fontSize: 11, color: "#6b7280", margin: "0 0 8px" }}>
                  <em>Les jours consécutifs d'un même bloc (week-end, pont, férié) sont comptés comme une seule occurrence. L'écart est calculé entre la fin d'une occurrence et le début de la suivante.</em>
                </p>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#fce7f3" }}>
                      <th style={{ padding: "5px 8px", textAlign: "left", borderBottom: "2px solid #f9a8d4" }}>Médecin</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #f9a8d4" }}>Origine</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #f9a8d4" }}>Astreintes</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #f9a8d4" }}>Occurrences</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #f9a8d4" }}>Écart min (j)</th>
                      <th style={{ padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #f9a8d4" }}>Écart max (j)</th>
                      <th style={{ padding: "5px 8px", textAlign: "left", borderBottom: "2px solid #f9a8d4" }}>Détail écart min</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ecartRows.map((r, i) => {
                      const ecartMinColor = r.ecartMin === null ? "#6b7280"
                        : r.ecartMin <= 1 ? "#dc2626"
                        : r.ecartMin <= 2 ? "#ea580c"
                        : r.ecartMin <= 3 ? "#d97706"
                        : "#059669";
                      const ecartMinBg = r.ecartMin !== null && r.ecartMin <= 2 ? "#fef2f2" : "transparent";
                      return (
                        <tr key={r.key} style={{ background: i % 2 === 0 ? "#fff" : "#fdf2f8" }}>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fce7f3" }}>{r.nom} {r.prenom}</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fce7f3", textAlign: "center", fontSize: 11 }}>{r.origine}</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fce7f3", textAlign: "center", fontWeight: 600 }}>{r.nbAstreintes}</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fce7f3", textAlign: "center" }}>{r.nbOccurrences}</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fce7f3", textAlign: "center", fontWeight: 700, color: ecartMinColor, background: ecartMinBg }}>
                            {r.ecartMin !== null ? r.ecartMin : "—"}
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fce7f3", textAlign: "center", fontWeight: 600, color: "#1e40af" }}>
                            {r.ecartMax !== null ? r.ecartMax : "—"}
                          </td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #fce7f3", fontSize: 11, color: "#6b7280" }}>
                            {r.pairMin || "occurrence unique"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}

          </div>{/* fin ref thoRecapRef */}

          {/* ═══ PAGE 2 PDF : DESIDERATA DES 4 MOIS (masqué à l'écran, visible uniquement pour capture PDF) ═══ */}
          <div ref={thoDesidRef} style={{ position: "absolute", left: "-9999px", top: "0", background: "#fff", padding: 10, width: "900px" }}>
            <div style={{ marginBottom: 8, paddingBottom: 6, borderBottom: "2px solid #374151" }}>
              <h4 style={{ margin: "0 0 3px", fontSize: 14, color: "#111827" }}>
                Astreinte Thoracique — Desiderata du quadrimestre
              </h4>
              <p style={{ margin: 0, fontSize: 11, color: "#374151" }}>
                <strong>Quadrimestre :</strong>{" "}
                {thoAllMoisCles.map(m => {
                  const [y, mo] = m.split("-");
                  const moisNoms = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
                  return `${moisNoms[parseInt(mo, 10)]} ${y}`;
                }).join(", ")}
                {" "}&mdash;{" "}
                <strong>Généré le :</strong>{" "}
                {new Date().toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
            {/* Légende */}
            <div style={{ display: "flex", gap: 12, marginBottom: 6, fontSize: 10, alignItems: "center" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#dcfce7", border: "1px solid #86efac", borderRadius: 2 }}></span> Souhaite absolument</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#fff", border: "1px solid #d1d5db", borderRadius: 2 }}></span> Disponible</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 2 }}></span> Peu disponible</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#fecaca", border: "1px solid #f87171", borderRadius: 2 }}></span> Indisponible</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#e5e7eb", border: "1px solid #9ca3af", borderRadius: 2 }}></span> Non renseigné</span>
            </div>
            {(() => {
              const desid = thoPrepData.desiderataParMedecin || {};
              const moisNoms = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
              // Construire la liste triée des médecins
              const allMeds = Object.entries(desid).map(([key, d]) => ({
                key, nom: d.nom, prenom: d.prenom,
                isInterieur: thoPrepData.interieursEquite.some(m => medKey(m.nom, m.prenom) === key),
                isNguyen: key === thoPrepData.nguyenMedKey,
              }));
              allMeds.sort((a, b) => a.nom.localeCompare(b.nom, 'fr') || a.prenom.localeCompare(b.prenom, 'fr'));

              const pad2Local = (n) => String(n).padStart(2, "0");
              const getCode = (valeurs, dateStr) => {
                const val = valeurs?.[dateStr];
                if (!val && val !== 0) return -1; // non renseigné
                const code = typeof val === "object" && val !== null ? val.code : val;
                return typeof code === "number" ? code : -1;
              };
              const codeColor = (code) => {
                if (code === 1) return { bg: "#dcfce7", border: "#86efac" }; // souhaite
                if (code === 2) return { bg: "#fff", border: "#d1d5db" }; // disponible
                if (code === 3) return { bg: "#fef3c7", border: "#fbbf24" }; // peu dispo
                if (code === 0) return { bg: "#fecaca", border: "#f87171" }; // indisponible
                return { bg: "#e5e7eb", border: "#9ca3af" }; // non renseigné
              };
              const getDowName = (y, m, d) => {
                const dow = new Date(y, m - 1, d).getDay();
                return ["D", "L", "M", "M", "J", "V", "S"][dow];
              };
              const isWeekend = (y, m, d) => {
                const dow = new Date(y, m - 1, d).getDay();
                return dow === 0 || dow === 6;
              };

              return thoAllMoisCles.map((mois) => {
                const [y, mo] = mois.split("-").map(Number);
                const nbJours = new Date(y, mo, 0).getDate();
                const jours = [];
                for (let d = 1; d <= nbJours; d++) jours.push(d);

                return (
                  <div key={mois} style={{ marginBottom: 8 }}>
                    <h5 style={{ margin: "0 0 3px", fontSize: 11, color: "#1f2937", fontWeight: 700 }}>
                      {moisNoms[mo]} {y}
                    </h5>
                    <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 8, tableLayout: "fixed" }}>
                      <thead>
                        <tr>
                          <th style={{ padding: "1px 2px", textAlign: "left", borderBottom: "1px solid #9ca3af", fontSize: 8, width: 90, minWidth: 90, whiteSpace: "nowrap", overflow: "hidden" }}>Médecin</th>
                          {jours.map(d => {
                            const we = isWeekend(y, mo, d);
                            return (
                              <th key={d} style={{ padding: "0px 0px", textAlign: "center", borderBottom: "1px solid #9ca3af", fontSize: 7, background: we ? "#f3f4f6" : "transparent", fontWeight: we ? 700 : 400 }}>
                                <div style={{ lineHeight: 1 }}>{getDowName(y, mo, d)}</div>
                                <div style={{ lineHeight: 1, fontWeight: 700 }}>{d}</div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {allMeds.map((med, i) => {
                          const valeurs = desid[med.key]?.valeurs || {};
                          const origBadge = med.isNguyen ? "N" : med.isInterieur ? "I" : "E";
                          const origColor = med.isNguyen ? "#7c3aed" : med.isInterieur ? "#059669" : "#2563eb";
                          return (
                            <tr key={med.key} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                              <td style={{ padding: "1px 2px", borderBottom: "1px solid #e5e7eb", fontSize: 7, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                <span style={{ display: "inline-block", width: 8, height: 8, lineHeight: "8px", textAlign: "center", borderRadius: 2, background: origColor, color: "#fff", fontSize: 6, marginRight: 2, fontWeight: 700 }}>{origBadge}</span>
                                {med.nom} {med.prenom}
                              </td>
                              {jours.map(d => {
                                const ds = `${y}-${pad2Local(mo)}-${pad2Local(d)}`;
                                const code = getCode(valeurs, ds);
                                const { bg, border } = codeColor(code);
                                return (
                                  <td key={d} style={{ padding: 0, borderBottom: "1px solid #e5e7eb", textAlign: "center" }}>
                                    <div style={{ width: "100%", height: 10, background: bg, border: `1px solid ${border}`, boxSizing: "border-box" }}></div>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              });
            })()}
          </div>

          <div style={{ marginTop: 20, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setThoPhase(1)}
              style={{ padding: "8px 20px", background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontSize: 14 }}
            >
              ← Retour Phase 1
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!thoRecapRef.current) return;
                try {
                  const html2canvas = (await import("html2canvas")).default;
                  const { jsPDF } = await import("jspdf");
                  const pageW = 210; // mm (A4)
                  const pageH = 297; // mm (A4)
                  const margin = 8; // mm
                  const usableW = pageW - 2 * margin;
                  const usableH = pageH - 2 * margin;
                  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

                  // --- PAGE 1 : Récapitulatif ---
                  const canvas1 = await html2canvas(thoRecapRef.current, { scale: 2, useCORS: true, backgroundColor: "#f0fdf4" });
                  const imgData1 = canvas1.toDataURL("image/png");
                  const aspect1 = canvas1.height / canvas1.width;
                  let imgW1 = usableW;
                  let imgH1 = imgW1 * aspect1;
                  if (imgH1 > usableH) { imgH1 = usableH; imgW1 = imgH1 / aspect1; }
                  const x1 = margin + (usableW - imgW1) / 2;
                  pdf.addImage(imgData1, "PNG", x1, margin, imgW1, imgH1);

                  // --- PAGE 2 : Desiderata ---
                  if (thoDesidRef.current) {
                    try {
                      // Rendre temporairement visible pour la capture html2canvas
                      const el2 = thoDesidRef.current;
                      el2.style.position = "static";
                      el2.style.left = "auto";
                      el2.style.top = "auto";
                      // Petit délai pour laisser le rendu se stabiliser
                      await new Promise(r => setTimeout(r, 200));
                      const canvas2 = await html2canvas(el2, {
                        scale: 2,
                        useCORS: true,
                        backgroundColor: "#ffffff",
                        scrollX: 0,
                        scrollY: -window.scrollY,
                      });
                      // Re-masquer immédiatement
                      el2.style.position = "absolute";
                      el2.style.left = "-9999px";
                      el2.style.top = "0";
                      if (canvas2.width > 0 && canvas2.height > 0) {
                        const imgData2 = canvas2.toDataURL("image/png");
                        const aspect2 = canvas2.height / canvas2.width;
                        let imgW2 = usableW;
                        let imgH2 = imgW2 * aspect2;
                        if (imgH2 > usableH) { imgH2 = usableH; imgW2 = imgH2 / aspect2; }
                        const x2 = margin + (usableW - imgW2) / 2;
                        pdf.addPage("a4", "portrait");
                        pdf.addImage(imgData2, "PNG", x2, margin, imgW2, imgH2);
                      }
                    } catch (err2) {
                      console.error("Erreur capture desiderata (page 2):", err2);
                      // Re-masquer en cas d'erreur aussi
                      if (thoDesidRef.current) {
                        thoDesidRef.current.style.position = "absolute";
                        thoDesidRef.current.style.left = "-9999px";
                        thoDesidRef.current.style.top = "0";
                      }
                    }
                  }

                  const moisLabel = thoAllMoisCles.length > 0
                    ? `${thoAllMoisCles[0]}_${thoAllMoisCles[thoAllMoisCles.length - 1]}`
                    : "quadrimestre";
                  pdf.save(`Recap_Astreinte_Thoracique_${moisLabel}.pdf`);
                } catch (err) {
                  console.error("Erreur génération PDF récap:", err);
                  notify("Erreur lors de la génération du PDF. Voir la console pour les détails.");
                }
              }}
              style={{ padding: "8px 20px", background: "#6366f1", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}
            >
              📄 Télécharger le récapitulatif (PDF)
            </button>
            <button
              type="button"
              onClick={() => navigate(withCurrentBack(`/responsable/gardes-brute?mois=${encodeURIComponent(thoAllMoisCles[0] || "2026-05")}&type=${encodeURIComponent(typeGarde)}`))}
              style={{ padding: "8px 24px", background: "#22c55e", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}
            >
              ✅ Consulter la liste brute d'astreintes
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          INTERFACE DE REVUE PAR PHASES — PR2 / PR1 / OPH
          ═══════════════════════════════════════════════════════════ */}

      {!isAstreinteThoracique && pr2Phase >= 1 && pr2Phase1Result && !pr2Phase2Result?.algorithme && (
        <div style={{ marginTop: 24, padding: 20, background: "#eff6ff", borderRadius: 10, border: "2px solid #3b82f6" }}>
          <h3 style={{ margin: "0 0 16px", color: "#1e40af", fontSize: 18 }}>
            📊 Phase 1 — Attribution initiale : 1 garde par médecin ({typeLabel})
          </h3>

          {/* ── Bandeau résumé ── */}
          {pr2Phase1Result.nonServis.length === 0 ? (
            <div style={{ padding: "10px 16px", background: "#d1fae5", borderRadius: 8, border: "1px solid #6ee7b7", marginBottom: 14, fontSize: 14 }}>
              <strong>✅ Tous les médecins éligibles ({pr2Phase1Result.servis.length}) ont reçu une garde.</strong>
              <span style={{ marginLeft: 12, color: "#374151" }}>
                Jours restants à attribuer en Phase 2 : <strong>{pr2Phase1Result.joursLibres.length}</strong>
              </span>
            </div>
          ) : (
            <div style={{ padding: "10px 16px", background: "#fef3c7", borderRadius: 8, border: "1px solid #fbbf24", marginBottom: 14, fontSize: 14 }}>
              <strong>⚠️ {pr2Phase1Result.servis.length} / {pr2Phase1Result.detailParMedecin.length} médecins servis.</strong>
              <span style={{ marginLeft: 8, color: "#dc2626", fontWeight: 600 }}>
                {pr2Phase1Result.nonServis.length} non-servi(s) : {pr2Phase1Result.nonServis.map(d => `${d.nom} ${d.prenom}`).join(", ")}
              </span>
              <br />
              <span style={{ color: "#374151" }}>
                Jours restants à attribuer en Phase 2 : <strong>{pr2Phase1Result.joursLibres.length}</strong>
              </span>
            </div>
          )}

          {/* ── Bandeau croisement PR1/Digestif/Réanimation/Thoracique/Orthopédie ── */}
          {pr2ExternalLists && (pr2ExternalLists.pr1Liste || pr2ExternalLists.digestifListe || pr2ExternalLists.reanimationListe || pr2ExternalLists.thoraciqueListe || pr2ExternalLists.orthopedieListe) && (
            <div style={{
              padding: "10px 16px",
              background: pr2Phase1Result.conflitsEvites?.length > 0 ? "#fef3c7" : "#d1fae5",
              borderRadius: 8,
              border: `1px solid ${pr2Phase1Result.conflitsEvites?.length > 0 ? "#f59e0b" : "#6ee7b7"}`,
              marginBottom: 14,
              fontSize: 13,
            }}>
              <strong>🔀 Croisement actif avec les listes existantes :</strong>
              {pr2ExternalLists.pr1Liste && <span style={{ marginLeft: 8 }}>PR1 ({Object.keys(pr2ExternalLists.pr1Liste).filter(k => !k.startsWith("_")).length}j)</span>}
              {pr2ExternalLists.digestifListe && <span style={{ marginLeft: 8 }}>Digestif ({Object.keys(pr2ExternalLists.digestifListe).filter(k => !k.startsWith("_")).length}j)</span>}
              {pr2ExternalLists.reanimationListe && <span style={{ marginLeft: 8 }}>Réa ({Object.keys(pr2ExternalLists.reanimationListe).filter(k => !k.startsWith("_")).length}j)</span>}
              {pr2ExternalLists.thoraciqueListe && <span style={{ marginLeft: 8 }}>Astr. Tho ({Object.keys(pr2ExternalLists.thoraciqueListe).filter(k => !k.startsWith("_")).length}j)</span>}
              {pr2ExternalLists.orthopedieListe && <span style={{ marginLeft: 8 }}>Astr. Ort ({Object.keys(pr2ExternalLists.orthopedieListe).filter(k => !k.startsWith("_")).length}j)</span>}
              {pr2Phase1Result.conflitsEvites?.length > 0 ? (
                <div style={{ marginTop: 8 }}>
                  <strong style={{ color: "#92400e" }}>⚠️ {pr2Phase1Result.conflitsEvites.length} jour(s) exclu(s) (espacement insuffisant ou même jour avec une autre liste) :</strong>
                  <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                    {pr2Phase1Result.conflitsEvites.map((c, i) => (
                      <li key={i} style={{ marginBottom: 2, color: "#78350f" }}>
                        <strong>{c.nom} {c.prenom}</strong> : {c.date} exclu → {c.raison}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div style={{ marginTop: 4, color: "#065f46" }}>
                  ✅ Aucun problème d’espacement détecté — chaque attribution respecte l’écart minimal ({MIN_ECART_GARDES_PR2} j. sur le mois) vis-à-vis des listes PR1, digestif, réanimation, Tho et Ort.
                </div>
              )}
            </div>
          )}

          {/* ── Médecins n'ayant pas déposé de desiderata ── */}
          {missingMedecins.length > 0 && (
            <div style={{ padding: "8px 14px", background: "#fef2f2", borderRadius: 8, border: "1px solid #fca5a5", marginBottom: 14, fontSize: 12, color: "#991b1b" }}>
              <strong>ℹ️ Médecins n'ayant pas déposé de desiderata ({missingMedecins.length}) :</strong>{" "}
              {missingMedecins.join(", ")}
            </div>
          )}

          <p style={{ margin: "0 0 10px", fontSize: 12, color: "#6b7280" }}>
            Stratégie : médecins les plus contraints servis en premier • priorité code 1 (souhaite) puis code 2 (disponible) • jour le moins disputé choisi.
            {pr2ExternalLists && (
              <> • Espacement minimal <strong>{MIN_ECART_GARDES_PR2}</strong> j. (grille du mois) entre PR2 et toute activité du même médecin sur PR1, digestif, réanimation, astreintes Tho/Ort (et autres PR2 une fois posées) ; prise en compte du mois précédent si la liste PR2 fin de mois est fournie.</>
            )}
          </p>

          {/* ── Tableau par date (vue calendrier) ── */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#dbeafe" }}>
                  <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "2px solid #93c5fd", width: 40 }}>#</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "2px solid #93c5fd", width: 60 }}>Jour</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "2px solid #93c5fd", width: 110 }}>Date</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "2px solid #93c5fd" }}>Médecin attribué</th>
                  <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "2px solid #93c5fd", width: 100 }}>Niveau</th>
                  {pr2Phase1Result.gardesExternes && (
                    <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "2px solid #93c5fd", width: 130 }}>Gardes ext.</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {pr2PrepData && pr2PrepData.jours.map((dateStr, i) => {
                  const medKey = pr2Phase1Result.affectations[dateStr] || null;
                  const detail = medKey ? pr2Phase1Result.detailParMedecin.find(d => d.med === medKey) : null;
                  const ext = medKey && pr2Phase1Result.gardesExternes ? pr2Phase1Result.gardesExternes[medKey] : null;
                  const dateObj = new Date(dateStr);
                  const dow = dateObj.getDay(); // 0=dim, 6=sam
                  const isWE = dow === 0 || dow === 6;
                  const jourNom = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"][dow];
                  const dateAffichee = dateObj.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

                  return (
                    <tr key={dateStr} style={{
                      background: !medKey
                        ? "#fef2f2"          // rouge pâle : jour vide
                        : isWE
                          ? "#f0f9ff"        // bleu pâle : weekend
                          : i % 2 === 0 ? "#fff" : "#f8fafc",
                    }}>
                      <td style={{ padding: "5px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb", color: "#9ca3af", fontSize: 11 }}>
                        {i + 1}
                      </td>
                      <td style={{ padding: "5px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: isWE ? 700 : 400, color: isWE ? "#7c3aed" : "#374151", fontSize: 12 }}>
                        {jourNom}
                      </td>
                      <td style={{ padding: "5px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: isWE ? 600 : 400 }}>
                        {dateAffichee}
                      </td>
                      <td style={{ padding: "5px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: 500 }}>
                        {detail ? (
                          `${detail.nom} ${detail.prenom}`
                        ) : (
                          <span style={{ color: "#dc2626", fontWeight: 600, fontSize: 12 }}>
                            — pas de garde attribuée
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "5px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                        {detail && detail.codeAttribue ? (
                          <span style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 600,
                            background: detail.codeAttribue === 1 ? "#d1fae5" : "#dbeafe",
                            color: detail.codeAttribue === 1 ? "#065f46" : "#1e40af",
                          }}>
                            {detail.codeAttribue === 1 ? "✓ Souhaite" : "○ Disponible"}
                          </span>
                        ) : (
                          !medKey && <span style={{ color: "#dc2626", fontSize: 11 }}>—</span>
                        )}
                      </td>
                      {pr2Phase1Result.gardesExternes && (
                        <td style={{ padding: "5px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontSize: 11 }}>
                          {ext && ext.totalExterne > 0 ? (
                            <span style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: 6,
                              fontWeight: 600,
                              background: ext.totalExterne >= 3 ? "#fef2f2" : ext.totalExterne >= 2 ? "#fef3c7" : "#f0f9ff",
                              color: ext.totalExterne >= 3 ? "#dc2626" : ext.totalExterne >= 2 ? "#92400e" : "#1e40af",
                            }}>
                              {[
                                ext.gardesPR1 > 0 && `${ext.gardesPR1}PR1`,
                                ext.gardesDigestif > 0 && `${ext.gardesDigestif}Dig`,
                                ext.astreintesTho > 0 && `${ext.astreintesTho}Tho`,
                                ext.astreintesOrt > 0 && `${ext.astreintesOrt}Ort`,
                              ].filter(Boolean).join("+")}={ext.totalExterne}
                            </span>
                          ) : medKey ? (
                            <span style={{ color: "#9ca3af" }}>0</span>
                          ) : null}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Tableau récapitulatif par médecin (gardes cumulées) ── */}
          {pr2Phase1Result.gardesExternes && Object.values(pr2Phase1Result.gardesExternes).some(e => e.totalExterne > 0) && (
            <div style={{ marginTop: 14 }}>
              <h4 style={{ margin: "0 0 8px", fontSize: 14, color: "#1e40af" }}>
                📊 Cumul gardes/astreintes par médecin (PR1 + Dig + Tho + Ort + PR2)
              </h4>
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#ede9fe" }}>
                      <th style={{ padding: "5px 10px", textAlign: "left", borderBottom: "2px solid #c4b5fd" }}>Médecin</th>
                      <th style={{ padding: "5px 10px", textAlign: "center", borderBottom: "2px solid #c4b5fd", width: 55 }}>PR1</th>
                      <th style={{ padding: "5px 10px", textAlign: "center", borderBottom: "2px solid #c4b5fd", width: 55 }}>Dig</th>
                      <th style={{ padding: "5px 10px", textAlign: "center", borderBottom: "2px solid #c4b5fd", width: 65 }}>Tho <span style={{fontSize:9,fontWeight:400}}>×0.5</span></th>
                      <th style={{ padding: "5px 10px", textAlign: "center", borderBottom: "2px solid #c4b5fd", width: 65 }}>Ort <span style={{fontSize:9,fontWeight:400}}>×0.5</span></th>
                      <th style={{ padding: "5px 10px", textAlign: "center", borderBottom: "2px solid #c4b5fd", width: 55 }}>PR2</th>
                      <th style={{ padding: "5px 10px", textAlign: "center", borderBottom: "2px solid #c4b5fd", width: 65, fontWeight: 700 }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pr2Phase1Result.detailParMedecin
                      .slice()
                      .sort((a, b) => {
                        const extA = pr2Phase1Result.gardesExternes[a.med]?.totalExterne || 0;
                        const extB = pr2Phase1Result.gardesExternes[b.med]?.totalExterne || 0;
                        const totalA = extA + (a.jourAttribue ? 1 : 0);
                        const totalB = extB + (b.jourAttribue ? 1 : 0);
                        return totalB - totalA;
                      })
                      .map((d) => {
                        const ext = pr2Phase1Result.gardesExternes[d.med] || { gardesPR1: 0, gardesDigestif: 0, astreintesTho: 0, astreintesOrt: 0, totalExterne: 0 };
                        const pr2Count = d.jourAttribue ? 1 : 0;
                        const total = ext.totalExterne + pr2Count;
                        return (
                          <tr key={d.med} style={{ background: total >= 4 ? "#fef2f2" : total >= 3 ? "#fef3c7" : "#fff" }}>
                            <td style={{ padding: "4px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: 500 }}>
                              {d.nom} {d.prenom}
                            </td>
                            <td style={{ padding: "4px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: ext.gardesPR1 > 0 ? 700 : 400, color: ext.gardesPR1 > 0 ? "#7c3aed" : "#9ca3af" }}>
                              {ext.gardesPR1}
                            </td>
                            <td style={{ padding: "4px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: ext.gardesDigestif > 0 ? 700 : 400, color: ext.gardesDigestif > 0 ? "#059669" : "#9ca3af" }}>
                              {ext.gardesDigestif}
                            </td>
                            <td style={{ padding: "4px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: ext.astreintesTho > 0 ? 700 : 400, color: ext.astreintesTho > 0 ? "#0e7490" : "#9ca3af" }}>
                              {ext.astreintesTho}
                            </td>
                            <td style={{ padding: "4px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: ext.astreintesOrt > 0 ? 700 : 400, color: ext.astreintesOrt > 0 ? "#b45309" : "#9ca3af" }}>
                              {ext.astreintesOrt}
                            </td>
                            <td style={{ padding: "4px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: pr2Count > 0 ? 700 : 400, color: pr2Count > 0 ? "#1e40af" : "#9ca3af" }}>
                              {pr2Count}
                            </td>
                            <td style={{ padding: "4px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: 700, fontSize: 13, color: total >= 4 ? "#dc2626" : total >= 3 ? "#92400e" : "#1e3a5f" }}>
                              {total}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Médecins non servis ── */}
          {pr2Phase1Result.nonServis.length > 0 && (
            <div style={{ marginTop: 12, padding: "8px 14px", background: "#fef2f2", borderRadius: 8, border: "1px solid #fca5a5", fontSize: 13, color: "#991b1b" }}>
              <strong>⚠️ Médecins éligibles non servis (aucun jour libre) :</strong>{" "}
              {pr2Phase1Result.nonServis.map(d => `${d.nom} ${d.prenom}`).join(", ")}
            </div>
          )}

          {/* ── Statistiques Phase 1 ── */}
          <div style={{ marginTop: 12, padding: "8px 14px", background: "#f0f9ff", borderRadius: 8, fontSize: 12, color: "#374151" }}>
            <strong>Résumé :</strong>{" "}
            {pr2Phase1Result.servis.length} médecin(s) servi(s) sur {pr2Phase1Result.detailParMedecin.length} éligible(s) —{" "}
            {pr2Phase1Result.servis.filter(d => d.codeAttribue === 1).length} sur code 1 (souhaite),{" "}
            {pr2Phase1Result.servis.filter(d => d.codeAttribue === 2).length} sur code 2 (disponible).{" "}
            <strong>{pr2Phase1Result.joursLibres.length} jour(s) restant(s)</strong> à compléter en Phase 2.
            {pr2Phase1Result.gardesExternes && (() => {
              const medsAvecExt = Object.entries(pr2Phase1Result.gardesExternes).filter(([, e]) => e.totalExterne > 0);
              return medsAvecExt.length > 0 ? (
                <><br /><strong>📊 Arbitrage :</strong> {medsAvecExt.length} médecin(s) avaient déjà des activités dans les autres secteurs (PR1, digestif, réa, astreintes) — les moins chargés en externe ont été servis en priorité pour la 1<sup>re</sup> PR2.</>
              ) : null;
            })()}
          </div>

          {/* ── Boutons de navigation ── */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4, textAlign: "center", maxWidth: 560 }}>
              Passez à la Phase 2 ou générez la liste de garde avec l’état actuel (1<sup>re</sup> attribution, trous possibles).
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
              Choisissez l'algorithme pour la Phase 2 :
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => { setPr2Phase(0); setPr2Phase1Result(null); setPr2Phase2Result(null); setPr2PrepData(null); setPr2CrossCheck(null); setPr2ExternalLists(null); }}
                style={{ padding: "8px 20px", background: "#6b7280", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
              >
                ← Recommencer
              </button>
              <button
                type="button"
                onClick={async () => {
                  const p2 = phase2_deuxiemeGarde(pr2PrepData, pr2Phase1Result, pr2ExternalLists);
                  for (const log of p2.logs) console.log(log);
                  setPr2Phase2Result({ ...p2, algorithme: "classique" });
                  setPr2Phase(2);
                  setPr2CrossCheck(null);
                  try {
                    const extPR1 = pr2ExternalLists?.pr1Liste || null;
                    const extDig = pr2ExternalLists?.digestifListe || null;
                    const extRea = pr2ExternalLists?.reanimationListe || null;
                    const extTho = pr2ExternalLists?.thoraciqueListe || null;
                    const extOrt = pr2ExternalLists?.orthopedieListe || null;
                    if (extPR1 || extDig || extRea || extTho || extOrt) {
                      const cc = crossCheckGardes(p2.affectations, extPR1, extDig, extRea, extTho, extOrt, { jours: pr2PrepData.jours });
                      setPr2CrossCheck(cc);
                    }
                  } catch (err) { console.warn("⚠️ Erreur cross-check :", err); }
                }}
                style={{ padding: "10px 24px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
              >
                Phase 2 — Classique (2e garde par médecin)
              </button>
            </div>
            <div style={{ marginTop: 8, paddingTop: 12, borderTop: "1px solid #bfdbfe", width: "100%", maxWidth: 520, textAlign: "center" }}>
              <button
                type="button"
                onClick={() => enregistrerPr2BruteEtOuvrir({ titrePhase: "après Phase 1 uniquement (sans Phase 2)" })}
                style={{ padding: "10px 22px", background: "#fff", color: "#047857", border: "2px solid #10b981", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
              >
                📋 Générer la liste de garde (Phase 1, avec trous)
              </button>
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", maxWidth: 640, textAlign: "center", lineHeight: 1.45 }}>
              <strong>Classique</strong> : 2<sup>e</sup> garde PR2 par médecin si possible, priorité aux moins chargés au total, avec la même règle d’espacement ({MIN_ECART_GARDES_PR2} j.) vs PR1 / digestif / réa / Tho / Ort / autres PR2.
            </div>
          </div>
        </div>
      )}

      {isPr2Secteur && <Pr2NextDayWarnings affectations={pr2Phase2Result?.affectations || pr2Phase1Result?.affectations} />}
      {/* ═══ PHASE 2 PR2 : 2e garde pour chacun ═══ */}
      {equilibragePanel}

      {!isAstreinteThoracique && pr2Phase === 2 && pr2Phase2Result && pr2Phase2Result.algorithme !== "equilibrage_permanence" && pr2PrepData && (
        <div style={{ marginTop: 24 }}>
        <div ref={pr2PdfRefs[0]} style={{ padding: 20, background: pr2Phase2Result.algorithme === "engagement_progressif" ? "#ecfdf5" : "#f0fdf4", borderRadius: 10, border: `2px solid ${pr2Phase2Result.algorithme === "engagement_progressif" ? "#0f766e" : "#22c55e"}` }}>
          <h3 style={{ margin: "0 0 16px", color: pr2Phase2Result.algorithme === "engagement_progressif" ? "#0f766e" : "#166534", fontSize: 18 }}>
            📊 {pr2Phase2Result.algorithme === "engagement_progressif" ? "PR2 — Engagement progressif" : "Phase 2 — 2e garde par médecin"} ({typeLabel})
            <span style={{
              marginLeft: 12,
              padding: "3px 10px",
              borderRadius: 12,
              fontSize: 11,
              fontWeight: 700,
              background: pr2Phase2Result.algorithme === "engagement_progressif" ? "#0f766e" : "#2563eb",
              color: "#fff",
              verticalAlign: "middle",
            }}>
              {pr2Phase2Result.algorithme === "engagement_progressif" ? "ENGAGEMENT" : "CLASSIQUE"}
            </span>
          </h3>
          <p style={{ margin: "0 0 14px", fontSize: 12, color: "#4b5563", lineHeight: 1.5 }}>
            {pr2Phase2Result.algorithme === "engagement_progressif" ? (
              <>Attribution par engagement croissant : une PR2 chacun, puis deuxième, puis suivantes. Gardes externes : écarts testés <strong>{(pr2Phase2Result.ecartsGardes || []).join("→")}</strong> j. ; astreintes : J-1/J/J+1 interdit.</>
            ) : (
              <>Toutes les attributions respectent l’espacement minimal de <strong>{MIN_ECART_GARDES_PR2}</strong> j. sur le mois entre le même médecin et ses activités PR2, PR1, digestif, réanimation, astreintes Tho/Ort (et le mois précédent si liste PR2 fournie).</>
            )}
          </p>
          {isPr2Secteur && (
            <p style={{ margin: "0 0 14px", fontSize: 12 }}>
              <a href="#rappel-pr2-doc" onClick={scrollToRappelPr2Doc} style={{ color: "#1d4ed8", fontWeight: 700 }}>
                ↑ Rappel PR2 (tête de page) : modalités, définition des écarts et des conflits
              </a>
            </p>
          )}

          {/* ── Bandeau résumé ── */}
          <div style={{ padding: "10px 16px", background: "#d1fae5", borderRadius: 8, border: `1px solid ${"#6ee7b7"}`, marginBottom: 14, fontSize: 14 }}>
            {pr2Phase2Result.algorithme === "engagement_progressif" ? (
              <>
                <strong>{pr2Phase2Result.joursAttribues.length} / {pr2PrepData.jours.length} jour(s) attribué(s) par engagement progressif.</strong>
                <br />
              </>
            ) : (
              <>
                <strong>{pr2Phase2Result.servisP2.length} / {pr2Phase2Result.detailPhase2.length} médecin(s) ont reçu une 2e garde{pr2Phase2Result.servisP2.length > 0 ? ` : ${pr2Phase2Result.servisP2.map(d => `${d.nom} ${d.prenom}`).join(", ")}` : ""}.</strong>
                <br />
              </>
            )}
            <span style={{ color: "#374151" }}>
              Jours attribués : <strong>{pr2Phase2Result.joursAttribues.length}</strong> / {pr2PrepData.jours.length}.
              {pr2Phase2Result.joursLibres.length > 0 ? (
                <> Jours restants : <strong>{pr2Phase2Result.joursLibres.length}</strong></>
              ) : (
                <> <strong style={{ color: "#059669" }}>Tous les jours sont attribués.</strong></>
              )}
            </span>
          </div>

          {/* ── Bandeau croisement Phase 2 ── */}
          {pr2Phase2Result.conflitsEvitesP2?.length > 0 && (
            <div style={{ padding: "10px 16px", background: "#fef3c7", borderRadius: 8, border: "1px solid #f59e0b", marginBottom: 14, fontSize: 13 }}>
              <strong style={{ color: "#92400e" }}>🔀 Phase 2 — {pr2Phase2Result.conflitsEvitesP2.length} jour(s) exclu(s) (espacement / listes externes / PR2) :</strong>
              <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                {pr2Phase2Result.conflitsEvitesP2.map((c, i) => (
                  <li key={i} style={{ marginBottom: 2, color: "#78350f" }}>
                    <strong>{c.nom} {c.prenom}</strong> : {c.date} exclu → {c.raison}
                  </li>
                ))}
              </ul>
            </div>
          )}


          {/* ══════════════════════════════════════════════════════════════ */}
          {/* ── Tableau par médecin : engagement progressif ── */}
          {/* ══════════════════════════════════════════════════════════════ */}
          {pr2Phase2Result.algorithme === "engagement_progressif" && pr2Phase2Result.gardesExternes && (
            <div style={{ marginBottom: 18 }}>
              <h4 style={{ margin: "0 0 10px", fontSize: 15, color: "#0f766e" }}>
                📊 Répartition pondérée par médecin — PR1 + Dig + Réa + PR2 + Ort + Tho
                {pr2Phase2Result.stats?.cible && (
                  <span style={{ marginLeft: 12, fontSize: 12, fontWeight: 400, color: "#6b7280" }}>
                    (gardes PR2 par engagement déclaré)
                  </span>
                )}
              </h4>
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#ccfbf1" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "2px solid #5eead4" }}>Médecin</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "2px solid #5eead4", width: 55 }}>PR1</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "2px solid #5eead4", width: 55 }}>Dig</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "2px solid #5eead4", width: 55 }}>Réa</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "2px solid #5eead4", width: 65 }}>Tho <span style={{fontSize:9,fontWeight:400}}>×0.5</span></th>
                      <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "2px solid #5eead4", width: 65 }}>Ort <span style={{fontSize:9,fontWeight:400}}>×0.5</span></th>
                      <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "2px solid #5eead4", width: 85, fontWeight: 700 }}>Ext total</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "2px solid #5eead4", width: 110 }}>PR2 (nb/pts)</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "2px solid #5eead4", width: 70 }}>Souhaité</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "2px solid #5eead4", width: 80, fontWeight: 800 }}>TOTAL pts</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "2px solid #5eead4" }}>Dates PR2</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pr2Phase2Result.detailPhase2
                      .slice()
                      .sort((a, b) => {
                        const extA = pr2Phase2Result.gardesExternes?.[a.med]?.totalExterne || 0;
                        const extB = pr2Phase2Result.gardesExternes?.[b.med]?.totalExterne || 0;
                        if (extB !== extA) return extB - extA;
                        if (b.totalGlobal !== a.totalGlobal) return b.totalGlobal - a.totalGlobal;
                        return a.med.localeCompare(b.med);
                      })
                      .map((d) => {
                        const ext = pr2Phase2Result.gardesExternes[d.med] || { gardesPR1: 0, gardesDigestif: 0, gardesReanimation: 0, astreintesTho: 0, astreintesOrt: 0, totalExterne: 0 };
                        const rea = ext.gardesReanimation || 0;
                        const dig = ext.gardesDigestif || 0;
                        const datesPR2 = d.datesPR2 || Object.entries(pr2Phase2Result.affectations)
                          .filter(([, m]) => m === d.med)
                          .map(([date]) => date)
                          .sort();
                        const cibleAtteinte = d.pr2Cible !== undefined && (d.totalPR2Pondere || d.totalPR2 || 0) >= d.pr2Cible;
                        const totalBg = d.totalGlobal >= 5 ? "#fef2f2" : d.totalGlobal >= 4 ? "#fef3c7" : d.totalGlobal >= 3 ? "#f5f3ff" : "#fff";
                        return (
                          <tr key={d.med} style={{ background: totalBg }}>
                            <td style={{ padding: "6px 12px", borderBottom: "1px solid #e5e7eb", fontWeight: 600 }}>
                              {d.nom} {d.prenom}
                            </td>
                            <td style={{ padding: "6px 12px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: ext.gardesPR1 > 0 ? 700 : 400, color: ext.gardesPR1 > 0 ? "#7c3aed" : "#9ca3af", fontSize: 14 }}>
                              {ext.gardesPR1}
                            </td>
                            <td style={{ padding: "6px 12px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: ext.gardesDigestif > 0 ? 700 : 400, color: ext.gardesDigestif > 0 ? "#059669" : "#9ca3af", fontSize: 14 }}>
                              {dig.toFixed(2)}
                            </td>
                            <td style={{ padding: "6px 12px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: rea > 0 ? 700 : 400, color: rea > 0 ? "#1d4ed8" : "#9ca3af", fontSize: 14 }}>
                              {rea.toFixed(2)}
                            </td>
                            <td style={{ padding: "6px 12px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: ext.astreintesTho > 0 ? 700 : 400, color: ext.astreintesTho > 0 ? "#0e7490" : "#9ca3af", fontSize: 14 }}>
                              {(ext.astreintesTho || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: "6px 12px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: ext.astreintesOrt > 0 ? 700 : 400, color: ext.astreintesOrt > 0 ? "#b45309" : "#9ca3af", fontSize: 14 }}>
                              {(ext.astreintesOrt || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: "6px 12px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: 700, color: "#334155", fontSize: 13 }}>
                              {(ext.totalExterne || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: "6px 12px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontSize: 13 }}>
                              <span style={{ fontWeight: 700, color: d.totalPR2 > 0 ? "#1e40af" : "#9ca3af" }}>{d.totalPR2}</span>
                              <span style={{ color: "#9ca3af", fontSize: 11 }}> / {(d.totalPR2Pondere || d.totalPR2 || 0).toFixed(2)}</span>
                              {d.pr2Cible !== undefined && (
                                <span style={{ color: "#9ca3af", fontSize: 11 }}> (cible {d.pr2Cible})</span>
                              )}
                              {cibleAtteinte && d.pr2Cible > 0 && <span style={{ marginLeft: 4, color: "#059669" }}>✓</span>}
                            </td>
                            <td style={{ padding: "6px 12px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontSize: 12, color: "#6b7280" }}>
                              {d.nbGardesSouhaite}
                            </td>
                            <td style={{ padding: "6px 12px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontWeight: 800, fontSize: 15, color: d.totalGlobal >= 5 ? "#dc2626" : d.totalGlobal >= 4 ? "#92400e" : "#1e3a5f" }}>
                              {(d.totalGlobal || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: "6px 12px", borderBottom: "1px solid #e5e7eb", fontSize: 11, color: "#6b7280" }}>
                              {datesPR2.length > 0 ? datesPR2.map(date => {
                                const dateObj = new Date(date);
                                const jour = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"][dateObj.getDay()];
                                return `${dateObj.getDate()}/${dateObj.getMonth()+1} (${jour})`;
                              }).join(", ") : "—"}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* ── Bandeau vérification croisement PR2 vs listes externes ── */}
          {pr2CrossCheck && (
            <div style={{
              marginTop: 14,
              padding: "12px 16px",
              borderRadius: 10,
              border: pr2CrossCheck.conflits.length > 0 ? "2px solid #ef4444" : "2px solid #22c55e",
              background: pr2CrossCheck.conflits.length > 0 ? "#fef2f2" : "#f0fdf4",
            }}>
              <h4 style={{ margin: "0 0 8px 0", fontSize: 14, color: pr2CrossCheck.conflits.length > 0 ? "#dc2626" : "#16a34a" }}>
                🔀 Vérification croisée PR2 vs {pr2CrossCheck.resume.listesDisponibles.join(" / ")}
              </h4>
              <p style={{ margin: "0 0 8px", fontSize: 12, color: "#4b5563" }}>
                Contrôle cohérent avec l’algorithme : écart minimal <strong>{MIN_ECART_GARDES_PR2}</strong> j. sur la grille du mois entre PR2 et chaque activité du même médecin (PR1, digestif, réanimation, Tho, Ort, autres PR2).
              </p>

              {pr2CrossCheck.alertes.map((a, i) => (
                <div key={i} style={{ fontSize: 13, marginBottom: 4, fontWeight: 600 }}>{a}</div>
              ))}

              {pr2CrossCheck.conflits.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10, fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#fecaca" }}>
                      <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "2px solid #ef4444" }}>Médecin</th>
                      <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "2px solid #ef4444" }}>Type</th>
                      <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "2px solid #ef4444" }}>Détail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pr2CrossCheck.conflits.map((c, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fef2f2" }}>
                        <td style={{ padding: "5px 10px", borderBottom: "1px solid #fecaca", fontWeight: 600 }}>
                          {c.medecin.replace(/_/g, " ")}
                        </td>
                        <td style={{ padding: "5px 10px", borderBottom: "1px solid #fecaca" }}>
                          <span style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 600,
                            background: c.type === "meme_jour" ? "#fee2e2" : "#fef3c7",
                            color: c.type === "meme_jour" ? "#991b1b" : "#92400e",
                          }}>
                            {c.type === "meme_jour" ? "⛔ Même jour" : "⚠️ Espacement"}
                          </span>
                        </td>
                        <td style={{ padding: "5px 10px", borderBottom: "1px solid #fecaca", fontSize: 11 }}>
                          {c.detail || (c.type === "meme_jour"
                            ? `${c.date} — ${(c.listes || []).join(" & ")}`
                            : `${c.date}`)
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          </div>{/* ═══ FIN PAGE 1 PDF ═══ */}

          {/* ═══ PAGE 2 PDF : Diagnostics (non-servis) ═══ */}
          <div ref={pr2PdfRefs[1]} style={{ padding: "12px 0" }}>
          {/* ── Médecins non servis (0 garde PR2) — avec diagnostic détaillé ── */}
          {pr2Phase2Result.algorithme === "engagement_progressif" && pr2Phase2Result.gardesExternes && (() => {
                  // Exclure les médecins qui n'ont pas rempli leurs desiderata (pas pertinent)
                  const nonServis = pr2Phase2Result.detailPhase2.filter(
                    d => d.totalPR2 === 0 && d.raisonNonServi && !d.raisonNonServi.exclure
                  );
                  if (nonServis.length === 0) return null;
                  return (
                    <div style={{ marginTop: 16, padding: "14px 16px", background: "#fef2f2", borderRadius: 10, border: "1px solid #fca5a5" }}>
                      <h4 style={{ margin: "0 0 12px", fontSize: 15, color: "#991b1b" }}>
                        ⚠️ {nonServis.length} médecin(s) sans garde PR2 après engagement progressif — Diagnostic
                      </h4>
                      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: "#fee2e2" }}>
                            <th style={{ padding: "8px 10px", textAlign: "left", borderBottom: "2px solid #fca5a5" }}>Médecin</th>
                            <th style={{ padding: "8px 10px", textAlign: "center", borderBottom: "2px solid #fca5a5", width: 90 }}>Gardes ext.</th>
                            <th style={{ padding: "8px 10px", textAlign: "center", borderBottom: "2px solid #fca5a5", width: 70 }}>Souhaité</th>
                            <th style={{ padding: "8px 10px", textAlign: "center", borderBottom: "2px solid #fca5a5", width: 70 }}>Cible PR2</th>
                            <th style={{ padding: "8px 10px", textAlign: "left", borderBottom: "2px solid #fca5a5" }}>Raison de non-attribution</th>
                          </tr>
                        </thead>
                        <tbody>
                          {nonServis.map((d) => {
                            const ext = pr2Phase2Result.gardesExternes[d.med] || { gardesPR1: 0, gardesDigestif: 0, gardesReanimation: 0, astreintesTho: 0, astreintesOrt: 0, totalExterne: 0 };
                            const r = d.raisonNonServi;
                            const extParts = [];
                            if (ext.gardesPR1 > 0) extParts.push(`${ext.gardesPR1}PR1`);
                            if (ext.gardesDigestif > 0) extParts.push(`${ext.gardesDigestif}Dig`);
                            if (ext.gardesReanimation > 0) extParts.push(`${ext.gardesReanimation}Réa`);
                            if (ext.astreintesTho > 0) extParts.push(`${ext.astreintesTho}Tho×0.5`);
                            if (ext.astreintesOrt > 0) extParts.push(`${ext.astreintesOrt}Ort×0.5`);
                            const extLabel = extParts.length > 0 ? extParts.join("+") + `=${ext.totalExterne}` : "0";

                            // Icône + couleur selon le code raison
                            let icon = "❓";
                            let bgColor = "#fff";
                            if (r) {
                              if (r.code === "DEJA_AU_DESSUS_CIBLE") { icon = "🎯"; bgColor = "#fef3c7"; }
                              else if (r.code === "AUCUNE_DISPONIBILITE") { icon = "📅"; bgColor = "#fce7f3"; }
                              else if (r.code === "TOUS_BLOQUES") { icon = "🔀"; bgColor = "#fff7ed"; }
                              else if (r.code === "BLOCAGE_COLLECTIF") { icon = "⚖️"; bgColor = "#fff7ed"; }
                              else if (r.code === "CONCURRENCE") { icon = "👥"; bgColor = "#eff6ff"; }
                              else if (r.code === "MIXTE") { icon = "🔀👥"; bgColor = "#faf5ff"; }
                            }

                            return (
                              <tr key={d.med} style={{ background: bgColor }}>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #fecaca", fontWeight: 600 }}>
                                  {d.nom} {d.prenom}
                                </td>
                                <td style={{ padding: "6px 10px", textAlign: "center", borderBottom: "1px solid #fecaca", fontSize: 12, fontWeight: 600 }}>
                                  {extLabel}
                                </td>
                                <td style={{ padding: "6px 10px", textAlign: "center", borderBottom: "1px solid #fecaca" }}>
                                  {d.nbGardesSouhaite}
                                </td>
                                <td style={{ padding: "6px 10px", textAlign: "center", borderBottom: "1px solid #fecaca" }}>
                                  {d.pr2Cible}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #fecaca", fontSize: 12 }}>
                                  {r ? (
                                    <div>
                                      <span style={{ fontWeight: 700 }}>{icon} {r.label}</span>
                                      <div style={{ marginTop: 3, color: "#6b7280", fontSize: 11 }}>
                                        {r.detail}
                                      </div>
                                      {r.joursDispoList && r.joursDispoList.length > 0 && (
                                        <div style={{ marginTop: 3, fontSize: 11, color: "#4b5563" }}>
                                          📅 Jour(s) demandé(s) : <strong>{r.joursDispoList.join(", ")}</strong>
                                        </div>
                                      )}
                                      {r.explication && (
                                        <div style={{ marginTop: 3, fontSize: 11, color: "#6b7280", fontStyle: "italic" }}>
                                          ℹ️ {r.explication}
                                        </div>
                                      )}
                                      {r.detailConflits && r.detailConflits.length > 0 && (
                                        <div style={{ marginTop: 3, color: "#92400e", fontSize: 11, fontStyle: "italic" }}>
                                          Conflits détaillés : {r.detailConflits.join(", ")}
                                        </div>
                                      )}
                                      {r.arbitrages && r.arbitrages.length > 0 && (
                                        <div style={{ marginTop: 5, padding: "6px 8px", background: "rgba(255,255,255,0.7)", borderRadius: 6, border: "1px solid #e5e7eb" }}>
                                          <span style={{ fontSize: 11, color: "#4b5563", fontWeight: 700 }}>Arbitrage collectif par charge :</span>
                                          <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 11, color: "#374151" }}>
                                            {r.arbitrages.map((a, i) => (
                                              <li key={i} style={{ marginBottom: 3 }}>
                                                Tour {a.engagement} : {a.flow}/{a.medecins?.length || 0} attribution(s) possible(s)
                                                {a.nonAssigned && a.nonAssigned.length > 0 && (
                                                  <span style={{ color: "#6b7280" }}> — non retenu : {a.nonAssigned.map(m => m.replace(/_/g, " ")).join(", ")}</span>
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      {r.gagnants && r.gagnants.length > 0 && (
                                        <div style={{ marginTop: 5, padding: "6px 8px", background: "rgba(255,255,255,0.7)", borderRadius: 6, border: "1px solid #e5e7eb" }}>
                                          <span style={{ fontSize: 11, color: "#4b5563", fontWeight: 700 }}>Médecin(s) ayant obtenu ces jours à sa place :</span>
                                          <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 11, color: "#374151" }}>
                                            {r.gagnants.map((g, i) => (
                                              <li key={i} style={{ marginBottom: 3 }}>
                                                <strong>{g.nom} {g.prenom}</strong>
                                                {" — "}total pondéré : <strong>{g.totalMin === g.totalMax ? g.totalMin : `${g.totalMin}–${g.totalMax}`}</strong>
                                                {g.extDetail && <span style={{ color: "#6b7280" }}> ({g.extDetail} + {g.pr2}PR2)</span>}
                                                {" — "}{g.nb} jour{g.nb > 1 ? "s" : ""}{g.dates && g.dates.length > 0 && <span style={{ color: "#6b7280" }}> : {g.dates.join(", ")}</span>}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  ) : "Raison inconnue"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {/* ── Légende ── */}
                      <div style={{ marginTop: 10, fontSize: 11, color: "#6b7280", display: "flex", gap: 16, flexWrap: "wrap" }}>
                        <span>🎯 Déjà à la cible (assez de gardes/astreintes dans les autres secteurs)</span>
                        <span>📅 A demandé des gardes mais aucun jour coché disponible</span>
                        <span>🔀 Tous bloqués par espacement insuffisant (règle {MIN_ECART_GARDES_PR2} j. ou plus selon phase) ou listes externes</span>
                        <span>👥 Jours pris par des médecins ayant un total pondéré plus bas</span>
                        <span>⚖️ Blocage collectif arbitré par charge pondérée</span>
                        <span style={{ fontStyle: "italic" }}>Pondération : 1 garde = 1, 1 astreinte = 0.5</span>
                      </div>
                    </div>
                  );
                })()}

          </div>{/* ═══ FIN PAGE 2 PDF ═══ */}

          {/* ═══ PAGE 3 PDF : Calendrier ═══ */}
          <div ref={pr2PdfRefs[2]} style={{ overflowX: "auto", padding: "8px 0" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#dcfce7" }}>
                  <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "2px solid #86efac", width: 40 }}>#</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "2px solid #86efac", width: 60 }}>Jour</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "2px solid #86efac", width: 110 }}>Date</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "2px solid #86efac" }}>Médecin attribué</th>
                  <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "2px solid #86efac", width: 100 }}>Niveau</th>
                  <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "2px solid #86efac", width: 155 }}>Écart prev.</th>
                  {pr2Phase2Result.gardesExternes && (
                    <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "2px solid #86efac", width: 130 }}>Gardes ext.</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const prevDateByMed = {};
                  const previousActivityByDate = pr2Phase2Result.algorithme === "engagement_progressif"
                    ? buildGapSincePreviousByDate(
                        pr2PrepData.jours,
                        pr2Phase2Result.affectations,
                        pr2ExternalLists?.pr2PrevWeek || null,
                        { detailed: true, externalLists: pr2ExternalLists || {} }
                      )
                    : {};
                  const prevWeekRows = Object.entries(pr2ExternalLists?.pr2PrevWeek || {})
                    .filter(([d, m]) => /^\d{4}-\d{2}-\d{2}$/.test(d) && !!m)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([dateStr, medKey], i) => {
                      prevDateByMed[medKey] = dateStr;
                      const dateObj = new Date(`${dateStr}T00:00:00Z`);
                      const dow = dateObj.getUTCDay();
                      const jourNom = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"][dow];
                      const dateAffichee = dateObj.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
                      return {
                        isPrevWeek: true,
                        rank: `M-1.${i + 1}`,
                        dateStr,
                        medKey,
                        jourNom,
                        dateAffichee,
                      };
                    });

                  const monthRows = pr2PrepData.jours.map((dateStr, i) => {
                  const medKey = pr2Phase2Result.affectations[dateStr] || null;
                  const estPhase2 = medKey && (!pr2Phase1Result || !pr2Phase1Result.affectations[dateStr]);
                  const ext = medKey && pr2Phase2Result.gardesExternes ? pr2Phase2Result.gardesExternes[medKey] : null;
                  const dateObj = new Date(dateStr);
                  const dow = dateObj.getDay();
                  const isWE = dow === 0 || dow === 6;
                  const jourNom = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"][dow];
                  const dateAffichee = dateObj.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
                  const previousActivity = pr2Phase2Result.algorithme === "engagement_progressif"
                    ? previousActivityByDate[dateStr]
                    : null;
                  let ecartPrev = previousActivity?.ecart ?? null;
                  if (medKey && pr2Phase2Result.algorithme !== "engagement_progressif") {
                    const prev = prevDateByMed[medKey] || null;
                    if (prev) {
                      const prevMs = new Date(`${prev}T00:00:00Z`).getTime();
                      const curMs = new Date(`${dateStr}T00:00:00Z`).getTime();
                      ecartPrev = Math.round((curMs - prevMs) / 86400000);
                    }
                    prevDateByMed[medKey] = dateStr;
                  }

                  // Trouver le code de préférence
                  let codeAffiche = null;
                  if (medKey) {
                    const detP1 = pr2Phase1Result ? pr2Phase1Result.detailParMedecin.find(d => d.med === medKey) : null;
                    if (estPhase2) {
                      const detP2 = pr2Phase2Result.detailPhase2.find(d => d.med === medKey);
                      codeAffiche = detP2 ? detP2.codeGarde2 : null;
                    } else {
                      codeAffiche = detP1 ? detP1.codeAttribue : null;
                    }
                    // Fallback : lire directement depuis les desiderata si toujours null
                    if (!codeAffiche && pr2PrepData.getPref) {
                      codeAffiche = pr2PrepData.getPref(medKey, dateStr) || null;
                    }
                  }

                  return (
                    <tr key={dateStr} style={{
                      background: !medKey
                        ? "#fef2f2"
                        : estPhase2
                          ? "#d1fae5"
                          : isWE
                            ? "#f0f9ff"
                            : i % 2 === 0 ? "#fff" : "#f8fafc",
                    }}>
                      <td style={{ padding: "5px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb", color: "#9ca3af", fontSize: 11 }}>
                        {i + 1}
                      </td>
                      <td style={{ padding: "5px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: isWE ? 700 : 400, color: isWE ? "#7c3aed" : "#374151", fontSize: 12 }}>
                        {jourNom}
                      </td>
                      <td style={{ padding: "5px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: isWE ? 600 : 400 }}>
                        {dateAffichee}
                      </td>
                      <td style={{ padding: "5px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: estPhase2 ? 700 : 500 }}>
                        {medKey ? (
                          (() => {
                            const [nom, prenom] = medKey.split("_");
                            return (
                              <span>
                                {nom} {prenom}
                              </span>
                            );
                          })()
                        ) : (
                          <span style={{ color: "#dc2626", fontWeight: 600, fontSize: 12 }}>
                            — pas de garde attribuée
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "5px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                        {codeAffiche ? (
                          <span style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 600,
                            background: codeAffiche === 1 ? "#d1fae5" : "#dbeafe",
                            color: codeAffiche === 1 ? "#065f46" : "#1e40af",
                          }}>
                            {codeAffiche === 1 ? "✓ Souhaite" : "○ Disponible"}
                          </span>
                        ) : (
                          !medKey && <span style={{ color: "#dc2626", fontSize: 11 }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: "5px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontSize: 11 }}>
                        {!medKey ? (
                          <span style={{ color: "#9ca3af" }}>—</span>
                        ) : ecartPrev === null ? (
                          <span style={{ color: "#9ca3af" }}>1ère garde</span>
                        ) : previousActivity?.type === "astreinte" ? (
                          <span style={{
                            display: "inline-flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                            padding: "3px 8px",
                            borderRadius: 6,
                            fontWeight: 700,
                            background: ecartPrev < PR2_ASTREINTE_ECART_FIXE ? "#fee2e2" : "#e0f2fe",
                            color: ecartPrev < PR2_ASTREINTE_ECART_FIXE ? "#991b1b" : "#0e7490",
                            lineHeight: 1.15,
                          }}>
                            <span>{`J-${ecartPrev} · ${previousActivity.secteur}`}</span>
                            <span style={{ fontSize: 10, fontWeight: 500 }}>
                              {new Date(`${previousActivity.date}T00:00:00Z`).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}
                            </span>
                          </span>
                        ) : (
                          <span style={{
                            display: "inline-flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                            padding: "3px 8px",
                            borderRadius: 6,
                            fontWeight: 700,
                            background: ecartPrev >= 6 ? "#dcfce7" : ecartPrev >= 3 ? "#fef3c7" : "#fee2e2",
                            color: ecartPrev >= 6 ? "#166534" : ecartPrev >= 3 ? "#92400e" : "#991b1b",
                            lineHeight: 1.15,
                          }}>
                            <span>
                              {previousActivity ? `J-${ecartPrev} · ${previousActivity.secteur}` : `+${ecartPrev}j`}
                            </span>
                            {previousActivity && (
                              <span style={{ fontSize: 10, fontWeight: 500 }}>
                                {new Date(`${previousActivity.date}T00:00:00Z`).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      {pr2Phase2Result.gardesExternes && (
                        <td style={{ padding: "5px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb", fontSize: 11 }}>
                          {ext && ext.totalExterne > 0 ? (
                            <span style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: 6,
                              fontWeight: 600,
                              background: ext.totalExterne >= 3 ? "#fef2f2" : ext.totalExterne >= 2 ? "#fef3c7" : "#f0f9ff",
                              color: ext.totalExterne >= 3 ? "#dc2626" : ext.totalExterne >= 2 ? "#92400e" : "#1e40af",
                            }}>
                              {[
                                ext.gardesPR1 > 0 ? `${ext.gardesPR1.toFixed(2)} PR1` : null,
                                ext.gardesDigestif > 0 ? `${ext.gardesDigestif.toFixed(2)} Dig` : null,
                                ext.gardesReanimation > 0 ? `${ext.gardesReanimation.toFixed(2)} Réa` : null,
                                ext.astreintesTho > 0 ? `${ext.astreintesTho.toFixed(2)} Tho` : null,
                                ext.astreintesOrt > 0 ? `${ext.astreintesOrt.toFixed(2)} Ort` : null,
                              ].filter(Boolean).join(" + ")}
                              {` = ${(ext.totalExterne || 0).toFixed(2)}`}
                            </span>
                          ) : medKey ? (
                            <span style={{ color: "#9ca3af" }}>0</span>
                          ) : null}
                        </td>
                      )}
                    </tr>
                  );
                  });

                  const prevWeekRenderedRows = prevWeekRows.map((r) => (
                    <tr key={`prev-${r.dateStr}-${r.medKey}`} style={{ background: "#fff7ed" }}>
                      <td style={{ padding: "5px 10px", textAlign: "center", borderBottom: "1px solid #fed7aa", color: "#9a3412", fontSize: 11, fontWeight: 700 }}>
                        {r.rank}
                      </td>
                      <td style={{ padding: "5px 10px", borderBottom: "1px solid #fed7aa", fontWeight: 700, color: "#c2410c", fontSize: 12 }}>
                        {r.jourNom}
                      </td>
                      <td style={{ padding: "5px 10px", borderBottom: "1px solid #fed7aa", fontWeight: 600, color: "#9a3412" }}>
                        {r.dateAffichee}
                      </td>
                      <td style={{ padding: "5px 10px", borderBottom: "1px solid #fed7aa", fontWeight: 600, color: "#7c2d12" }}>
                        {(() => {
                          const [nom, prenom] = String(r.medKey).split("_");
                          return `${nom || ""} ${prenom || ""}`.trim();
                        })()}
                        <span style={{ marginLeft: 8, padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: "#f97316", color: "#fff" }}>
                          Semaine précédente
                        </span>
                      </td>
                      <td style={{ padding: "5px 10px", textAlign: "center", borderBottom: "1px solid #fed7aa" }}>
                        <span style={{ color: "#9a3412", fontSize: 11, fontWeight: 600 }}>Historique M-1</span>
                      </td>
                      <td style={{ padding: "5px 10px", textAlign: "center", borderBottom: "1px solid #fed7aa", fontSize: 11 }}>
                        <span style={{ color: "#9ca3af" }}>—</span>
                      </td>
                      {pr2Phase2Result.gardesExternes && (
                        <td style={{ padding: "5px 10px", textAlign: "center", borderBottom: "1px solid #fed7aa", fontSize: 11 }}>
                          <span style={{ color: "#9ca3af" }}>—</span>
                        </td>
                      )}
                    </tr>
                  ));

                  return [...prevWeekRenderedRows, ...monthRows];
                })()}
              </tbody>
            </table>
          </div>{/* ═══ FIN PAGE 3 PDF ═══ */}

          {/* ═══ PAGE 4 PDF : Desiderata ═══ */}
          <div ref={pr2PdfRefs[3]} style={{ padding: "8px 0" }}>

          {/* ── Desiderata du mois (comme pour le thoracique) ── */}
          {pr2PrepData && pr2PrepData.souhaits && (() => {
            const souhaits = pr2PrepData.souhaits;
            const jours = pr2PrepData.jours;
            if (!jours || jours.length === 0) return null;

            // Extraire le mois depuis le premier jour (ex: "2026-04-01")
            const [annee, moisStr] = jours[0].split("-");
            const moisNum = parseInt(moisStr, 10);
            const moisNoms = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

            // Liste triée des médecins
            const allMeds = Object.entries(souhaits)
              .map(([key, s]) => ({ key, nom: s.nom, prenom: s.prenom, nbGardes: s.nbGardes }))
              .sort((a, b) => a.nom.localeCompare(b.nom, "fr") || a.prenom.localeCompare(b.prenom, "fr"));

            const pad2 = (n) => String(n).padStart(2, "0");
            const getDowName = (dateStr) => {
              const dow = new Date(dateStr).getDay();
              return ["D", "L", "Ma", "Me", "J", "V", "S"][dow];
            };
            const isWeekend = (dateStr) => {
              const dow = new Date(dateStr).getDay();
              return dow === 0 || dow === 6;
            };
            const codeColor = (code) => {
              if (code === 1) return { bg: "#dcfce7", border: "#86efac", label: "S" }; // souhaite
              if (code === 2) return { bg: "#fff", border: "#d1d5db", label: "" }; // disponible
              if (code === 3) return { bg: "#fef3c7", border: "#fbbf24", label: "P" }; // peu dispo
              if (code === 0) return { bg: "#fecaca", border: "#f87171", label: "X" }; // indisponible
              return { bg: "#e5e7eb", border: "#9ca3af", label: "?" }; // non renseigné
            };

            // Jours sous forme de numéros
            const jourNums = jours.map(d => parseInt(d.split("-")[2], 10));

            return (
              <div>
                <div style={{ marginBottom: 8, paddingBottom: 6, borderBottom: "2px solid #374151" }}>
                  <h4 style={{ margin: "0 0 3px", fontSize: 14, color: "#111827" }}>
                    📋 Desiderata — Gardes {typeLabel} — {moisNoms[moisNum]} {annee}
                  </h4>
                  <p style={{ margin: 0, fontSize: 11, color: "#374151" }}>
                    <strong>{allMeds.length} médecin(s)</strong> ayant rempli leurs desiderata
                    {" — "}
                    <strong>Généré le :</strong>{" "}
                    {new Date().toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>

                {/* Légende */}
                <div style={{ display: "flex", gap: 12, marginBottom: 8, fontSize: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#dcfce7", border: "1px solid #86efac", borderRadius: 2 }}></span> Souhaite (S)</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#fff", border: "1px solid #d1d5db", borderRadius: 2 }}></span> Disponible</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 2 }}></span> Peu disponible (P)</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#fecaca", border: "1px solid #f87171", borderRadius: 2 }}></span> Indisponible (X)</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#e5e7eb", border: "1px solid #9ca3af", borderRadius: 2 }}></span> Non renseigné</span>
                </div>

                {/* Tableau desiderata */}
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 8, tableLayout: "fixed" }}>
                    <thead>
                      <tr>
                        <th style={{ padding: "1px 2px", textAlign: "left", borderBottom: "1px solid #9ca3af", fontSize: 8, width: 110, minWidth: 110, whiteSpace: "nowrap", overflow: "hidden" }}>Médecin</th>
                        <th style={{ padding: "1px 2px", textAlign: "center", borderBottom: "1px solid #9ca3af", fontSize: 7, width: 20 }}>Nb</th>
                        {jourNums.map((d, idx) => {
                          const we = isWeekend(jours[idx]);
                          return (
                            <th key={d} style={{ padding: "0px 0px", textAlign: "center", borderBottom: "1px solid #9ca3af", fontSize: 7, background: we ? "#f3f4f6" : "transparent", fontWeight: we ? 700 : 400 }}>
                              <div style={{ lineHeight: 1 }}>{getDowName(jours[idx])}</div>
                              <div style={{ lineHeight: 1, fontWeight: 700 }}>{d}</div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {allMeds.map((med, i) => {
                        const prefs = souhaits[med.key]?.souhaits || {};
                        return (
                          <tr key={med.key} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                            <td style={{ padding: "1px 2px", borderBottom: "1px solid #e5e7eb", fontSize: 7, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {med.nom} {med.prenom}
                            </td>
                            <td style={{ padding: "1px 2px", borderBottom: "1px solid #e5e7eb", fontSize: 7, textAlign: "center", fontWeight: 700, color: med.nbGardes > 0 ? "#1e40af" : "#9ca3af" }}>
                              {med.nbGardes || 0}
                            </td>
                            {jours.map((dateStr, idx) => {
                              const code = prefs[dateStr] ?? -1;
                              const { bg, border, label } = codeColor(code);
                              return (
                                <td key={idx} style={{ padding: 0, borderBottom: "1px solid #e5e7eb", textAlign: "center" }}>
                                  <div style={{ width: "100%", height: 11, background: bg, border: `1px solid ${border}`, boxSizing: "border-box", fontSize: 6, lineHeight: "11px", color: "#374151", fontWeight: 600 }}>{label}</div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          </div>{/* ═══ FIN PAGE 4 PDF ═══ */}

          {/* ── Boutons de navigation ── */}
          <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
            {/* Bouton télécharger PDF — 4 pages distinctes */}
            <button
              type="button"
              onClick={async () => {
                try {
                  const html2canvas = (await import("html2canvas")).default;
                  const { jsPDF } = await import("jspdf");
                  const pageW = 210;
                  const pageH = 297;
                  const margin = 8;
                  const usableW = pageW - 2 * margin;
                  const usableH = pageH - 2 * margin;
                  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

                  const pageBgs = ["#f0fdf4", "#ffffff", "#ffffff", "#ffffff"];

                  // Même technique exacte que le PDF thoracique (qui fonctionne) :
                  // - Rendre l'élément visible (position static)
                  // - Scroller vers lui
                  // - html2canvas avec paramètres MINIMAUX + scrollY: -window.scrollY
                  let isFirst = true;
                  for (let i = 0; i < pr2PdfRefs.length; i++) {
                    const el = pr2PdfRefs[i].current;
                    if (!el || el.offsetHeight === 0) continue;

                    // Technique thoracique page 2 : s'assurer que l'élément est en position static
                    const origPos = el.style.position;
                    const origLeft = el.style.left;
                    const origTop = el.style.top;
                    el.style.position = "static";
                    el.style.left = "auto";
                    el.style.top = "auto";

                    // Scroller vers l'élément
                    el.scrollIntoView({ block: "start", behavior: "instant" });
                    await new Promise(r => setTimeout(r, 200));

                    // Capturer — paramètres IDENTIQUES au thoracique (rien de plus)
                    const canvas = await html2canvas(el, {
                      scale: 2,
                      useCORS: true,
                      backgroundColor: pageBgs[i],
                      scrollX: 0,
                      scrollY: -window.scrollY,
                    });

                    // Restaurer la position
                    el.style.position = origPos;
                    el.style.left = origLeft;
                    el.style.top = origTop;

                    if (canvas.width === 0 || canvas.height === 0) continue;

                    if (!isFirst) pdf.addPage("a4", "portrait");
                    isFirst = false;

                    // Mise à l'échelle identique au thoracique
                    const aspect = canvas.height / canvas.width;
                    let imgW = usableW;
                    let imgH = imgW * aspect;
                    if (imgH > usableH) { imgH = usableH; imgW = imgH / aspect; }
                    const x = margin + (usableW - imgW) / 2;
                    pdf.addImage(canvas.toDataURL("image/png"), "PNG", x, margin, imgW, imgH);
                  }

                  const moisDateEq = pr2PrepData?.jours?.[0] ? new Date(pr2PrepData.jours[0]) : null;
                  const moisNomEq = moisDateEq ? moisDateEq.toLocaleDateString("fr-FR", { month: "long" }).replace(/^./, c => c.toUpperCase()) : (MOIS_CLE || "Gardes");
                  const anneeEq = moisDateEq ? moisDateEq.getFullYear() : "";
                  pdf.save(`Recap_PR2_Liste_Gardes_${moisNomEq}_${anneeEq}.pdf`);
                } catch (err) {
                  console.error("Erreur génération PDF PR2:", err);
                  notify("Erreur lors de la génération du PDF. Voir la console pour les détails.");
                }
              }}
              style={{ padding: "8px 20px", background: "#6366f1", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}
            >
              📄 Télécharger le récapitulatif (PDF)
            </button>
            {pr2Phase1Result && !pr2Phase2Result?.algorithme && (
              <button
                type="button"
                onClick={() => { setPr2Phase(1); setPr2Phase2Result(null); setPr2CrossCheck(null); }}
                style={{ padding: "8px 20px", background: "#6b7280", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
              >
                ← Retour Phase 1
              </button>
            )}
            <button
              type="button"
              onClick={() => { setPr2Phase(0); setPr2Phase1Result(null); setPr2Phase2Result(null); setPr2PrepData(null); setPr2CrossCheck(null); setPr2ExternalLists(null); }}
              style={{ padding: "8px 20px", background: "#6b7280", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
            >
              ↺ Recommencer
            </button>
            {pr2Phase2Result.joursLibres.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  notify("Phase suivante — à définir !");
                }}
                style={{ padding: "8px 24px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}
              >
                Valider → Phase suivante
              </button>
            )}
            <button
              type="button"
              onClick={() => enregistrerPr2BruteEtOuvrir({ titrePhase: "état actuel du workflow" })}
              style={{ padding: "8px 24px", background: "#22c55e", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}
            >
              📋 Générer la liste de garde (état actuel)
            </button>
          </div>

        </div>
      )}

      {!isLoading && !(isAstreinteThoracique && (thoPhase === 2 || thoPhase === 12)) && pr2Phase === 0 && (
        <>
          {isPr2Secteur && (
            <div
              style={{
                maxWidth: 760,
                margin: "24px auto 0",
                padding: "14px 18px",
                background: "#f8fafc",
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                fontSize: 13,
                color: "#334155",
                lineHeight: 1.55,
              }}
            >
              <h4 style={{ margin: "0 0 10px", fontSize: 15, color: "#0f172a" }}>
                ℹ️ Détails complémentaires — génération PR2
              </h4>
              <p style={{ margin: "0 0 10px", fontSize: 12, color: "#64748b" }}>
                Rappel des <strong>boutons</strong>, de l’<strong>écart</strong> et d’un <strong>conflit</strong> : bloc bleu <strong>« Rappel PR2 »</strong> juste <strong>sous le titre</strong> de la page — remonter en haut ou utiliser le lien sous les boutons.
              </p>
              <p style={{ margin: "0 0 10px" }}>
                <strong>Prérequis :</strong> les cinq listes du mois (PR1, digestif, réanimation, astreinte thoracique, astreinte orthopédie) doivent être chargées et non vides ; avec <strong>PR2 — Engagement progressif</strong>, elles déclenchent un diagnostic non bloquant et une confirmation explicite.
              </p>
              <p style={{ margin: "0 0 10px" }}>
                <strong>Règle d’espacement :</strong> pour chaque médecin, entre deux dates où il a une activité (PR2, PR1, digestif, réanimation, astreintes Tho/Ort), l’écart mesuré en <em>jours calendaires</em> suit le paramètre du générateur. Engagement progressif applique une relaxation 6→5→4→3 pour les gardes, et garde les astreintes sur une règle fixe J-1/J/J+1.
                {" "}Si des listes de fin de mois précédent sont fournies, le même contrôle s’applique aussi entre ces dates et le mois en cours.
              </p>
              <p style={{ margin: "0 0 10px" }}>
                <strong>Pondération par jour (charges et cible d’équilibrage) :</strong> pour agréger PR1, digestif, réanimation, astreintes Tho/Ort et PR2 dans les totaux « points », chaque date est multipliée par un coefficient : <strong>1</strong> du lundi au vendredi hors jour férié, <strong>1,5</strong> le samedi, <strong>2</strong> le dimanche ou tout <strong>jour férié</strong> (y compris un férié en semaine). Une <strong>garde</strong> compte <strong>1 ×</strong> ce coefficient ; une <strong>astreinte</strong> (Tho, Ort) compte <strong>0,5 ×</strong> ce coefficient. La somme pondérée sert aux charges affichées dans le récapitulatif.
              </p>
              <p style={{ margin: "0 0 10px" }}>
                <strong>Phase 1 :</strong> une première garde PR2 par médecin éligible (les plus contraints en premier), préférence code 1 (souhaite) puis code 2 (disponible), en respectant l’espacement ci-dessus et les règles calendrier PR2 (WE/fériés).
              </p>
              <p style={{ margin: "0 0 10px" }}>
                <strong>Phase 2 — Classique :</strong> attribue en principe une 2<sup>e</sup> garde PR2 à chaque médecin, en privilégiant les moins chargés au total.
              </p>
            </div>
          )}
        {isPr2Secteur && (
          <div style={{ width: "100%", textAlign: "center", marginTop: 20, marginBottom: 0 }}>
            <a
              href="#rappel-pr2-doc"
              onClick={scrollToRappelPr2Doc}
              style={{ color: "#1d4ed8", fontWeight: 700, fontSize: 14, textDecoration: "underline" }}
            >
              ↑ Rappel PR2 : modalités, écarts entre gardes et définition des conflits
            </a>
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            marginTop: 12,
            flexWrap: "wrap",
          }}
        >
          {canConstituteList && <button
            type="button"
            style={btnSecondary}
            disabled={isPr2Secteur && (nbGardesSavingKey !== null || equilibrageBusy)}
            onClick={async () => {
              if (!MOIS_CLE) {
                notify("Configuration du mois invalide.");
                return;
              }

              if (missingMedecins.length > 0) {
                const message = [
                  "Certains médecins n’ont pas encore soumis leurs desiderata :",
                  "",
                  ...missingMedecins.map((m) => `- ${m}`),
                  "",
                  isAstreinteThoracique
                    ? "La liste d'astreintes sera générée UNIQUEMENT avec les médecins ayant envoyé leurs desiderata."
                    : "La liste de garde sera générée UNIQUEMENT avec les médecins ayant envoyé leurs desiderata.",
                  isAstreinteThoracique
                    ? "Le nombre d'astreintes souhaité pour chaque médecin sera strictement respecté,"
                    : "Le nombre de gardes souhaité pour chaque médecin sera strictement respecté,",
                  isAstreinteThoracique
                    ? "aucune astreinte successive ne sera attribuée au même médecin,"
                    : "aucune garde successive ne sera attribuée au même médecin,",
                  isAstreinteThoracique
                    ? "et les astreintes seront espacées autant que possible en équilibrant les vendredis/samedis/dimanches."
                    : "et les gardes seront espacées autant que possible en équilibrant les vendredis/samedis/dimanches.",
                  "",
                  isAstreinteThoracique
                    ? "Voulez-vous tout de même générer / mettre à jour la liste d'astreintes ?"
                    : "Voulez-vous tout de même générer / mettre à jour la liste de garde ?",
                ].join("\n");

                const proceed = (await confirmDialog(message));
                if (!proceed) return;
              }

              try {
                // ================================================================
                // BRANCHEMENT ASTREINTE THORACIQUE : PHASE 1 uniquement
                // Le responsable revoit les blocs avant de passer aux phases suivantes
                // ================================================================
                if (isAstreinteThoracique) {
                  console.log("═══════════════════════════════════════════════════════════");
                  console.log("🚀 ALGORITHME ASTREINTE THORACIQUE — LANCEMENT PHASE 1");
                  console.log("═══════════════════════════════════════════════════════════");

                  const allMoisCles = moisOuverts.map((m) => String(m.mois_cle));
                  setThoAllMoisCles(allMoisCles);

                  // Charger desiderata de tous les mois
                  const desiderataParMois = {};
                  await Promise.all(
                    allMoisCles.map(async (mc) => {
                      const ep = `/api/v2/desiderata-astreinte-thoracique/${mc}`;
                      const r = await apiFetch(ep, { credentials: "include" });
                      if (!r.ok) { desiderataParMois[mc] = []; return; }
                      desiderataParMois[mc] = await r.json();
                      console.log(`✅ ${mc}: ${desiderataParMois[mc].length} desiderata`);
                    })
                  );

                  // Charger médecins
                  const resMed = await apiFetch("/api/v2/medecins-astreintes-thoracique", {
                    credentials: "include",
                  });
                  if (!resMed.ok) throw new Error(`Erreur médecins: HTTP ${resMed.status}`);
                  const medecinsTho = await resMed.json();

                  // Préparation commune
                  const prepData = preparerDonnees({ moisList: allMoisCles, medecins: medecinsTho, desiderataParMois });
                  for (const log of prepData.logs) console.log(log);
                  setThoPrepData(prepData);

                  // Charger les ajustements bonus/malus (les plus récents)
                  let ajustementsData = {};
                  try {
                    const resAjust = await apiFetch("/api/v2/ajustements-astreinte-thoracique", {
                      credentials: "include",
                    });
                    if (resAjust.ok) ajustementsData = await resAjust.json();
                  } catch (_e) { /* ignore */ }
                  console.log("📋 Ajustements chargés pour Phase 1 :", ajustementsData);

                  // Exécuter Phase 1 (blocs weekends/fériés) AVEC ajustements
                  const p1 = phase1_attribuerBlocs({ prepData, ajustements: ajustementsData });
                  for (const log of p1.logs) console.log(log);
                  setThoPhase1Result(p1);

                  // Initialiser les sélections modifiables (null = non attribué)
                  const selections = {};
                  p1.propositions.forEach((prop, idx) => {
                    selections[idx] = prop.choisiKey || "";
                  });
                  setThoPhase1Selections(selections);

                  // Passer en mode revue Phase 1
                  setThoJoursRestantsPack(null);
                  setThoPhase(1);
                  return;
                }

                // ================================================================
                // ALGORITHME PR2 — Phase 1 : 1 garde pour chacun
                // ================================================================

                // ✅ Endpoint des désidératas selon le type (PR1, PR2, OPH, INTERNE1, INTERNE2)
                let endpoint = `/api/v2/desiderata-pr2/${MOIS_CLE}`; // PR2 par défaut
                if (typeGarde === "PR1") endpoint = `/api/v2/desiderata-pr1/${MOIS_CLE}`;
                else if (typeGarde === "INTERNE1") endpoint = `/api/v2/desiderata-interne1/${MOIS_CLE}`;
                else if (typeGarde === "INTERNE2") endpoint = `/api/v2/desiderata-interne2/${MOIS_CLE}`;
                else if (typeGarde === "DIGESTIF") endpoint = `/api/v2/desiderata-digestif/${MOIS_CLE}`;
                else if (typeGarde === "REANIMATION") endpoint = `/api/v2/desiderata-reanimation/${MOIS_CLE}`;
                console.log(
                  `📥 Lecture des désidérata ${typeGarde} via ${endpoint}...`
                );
                const res = await apiFetch(endpoint, { credentials: "include" });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const desiderata = await res.json();

                // 🚀 Préparation des données
                const prepDataPR2 = preparerDonneesPR2(desiderata, YEAR, MONTH_NUM, { zeroGardesExcludes: isPr2Secteur });
                setPr2PrepData(prepDataPR2);
                console.log(`📋 Données préparées : ${prepDataPR2.eligibles.length} éligibles, ${prepDataPR2.jours.length} jours`);

                // 🔀 Charger les listes PR1, Digestif, Thoracique, Orthopédie pour le croisement dès Phase 1
                const externalLists = await loadExternalListsWithWarning(MOIS_CLE);
                if (externalLists === null && !window._bypassExternalCheck) {
                  return; // Listes externes manquantes, erreur API, ou bypass refusé
                }

                // Stocker les listes externes pour la Phase 2
                setPr2ExternalLists(externalLists);

                // 🚀 Phase 1 : 1 garde pour chacun (avec croisement PR1/Digestif)
                const p1 = phase1_uneGardeChacun(prepDataPR2, externalLists);
                for (const log of p1.logs) console.log(log);
                setPr2Phase1Result(p1);

                // Passer en mode revue Phase 1
                setPr2Phase(1);
              } catch (err) {
                console.error(`❌ Erreur génération ou sauvegarde liste ${typeGarde} :`, err);
                notify(
                  `Erreur lors de la génération de la liste ${isAstreinteThoracique ? "d'astreintes" : "de garde"}.\n${
                    err.message || ""
                  }`
                );
              }
            }}
          >
            {isAstreinteThoracique ? "🗎 Générer / mettre à jour la liste d'astreintes" : "🗎 Générer la liste (Classique)"}
          </button>}

          {!isAstreinteThoracique && typeGarde === "PR2" && (
            <button
              type="button"
              disabled={nbGardesSavingKey !== null || equilibrageBusy}
              style={{ ...btnSecondary, background: "#0f766e", color: "#fff" }}
              onClick={async () => {
                if (!MOIS_CLE) { notify("Configuration du mois invalide."); return; }
                setPr2ExternalDiagnostics([]);

                if (missingMedecins.length > 0) {
                  const message = [
                    "Certains médecins n'ont pas encore soumis leurs desiderata :",
                    "", ...missingMedecins.map((m) => `- ${m}`), "",
                    "La liste de garde sera générée UNIQUEMENT avec les médecins ayant envoyé leurs desiderata.",
                    "", "Continuer ?",
                  ].join("\n");
                  if (!(await confirmDialog(message))) return;
                }

                try {
                  const endpoint = `/api/v2/desiderata-pr2/${MOIS_CLE}`;
                  console.log(`📥 [Engagement progressif] Lecture des désidérata PR2 via ${endpoint}...`);
                  const res = await apiFetch(endpoint, { credentials: "include" });
                  if (!res.ok) throw new Error(`HTTP ${res.status}`);
                  const desiderata = await res.json();

                  const prepDataEngagement = preparerDonneesPR2(desiderata, YEAR, MONTH_NUM, { zeroGardesExcludes: true });
                  setPr2PrepData(prepDataEngagement);
                  console.log(`📋 [Engagement progressif] ${prepDataEngagement.eligibles.length} éligibles, ${prepDataEngagement.jours.length} jours`);

                  const { lists: externalLists, diagnostics } = await loadExternalListsWithDiagnostics(MOIS_CLE);
                  setPr2ExternalDiagnostics(diagnostics);
                  const warnings = diagnostics.filter((d) => d.severity !== "ok");
                  if (warnings.length > 0) {
                    const message = [
                      "Diagnostic des listes externes PR2 :",
                      "",
                      ...diagnostics.map((d) => `${d.severity === "ok" ? "✅" : "⚠️"} ${d.message}`),
                      "",
                      "La génération peut continuer avec les données disponibles.",
                      "Continuer malgré ces alertes ?",
                    ].join("\n");
                    if (!(await confirmDialog(message))) return;
                  }
                  setPr2ExternalLists(externalLists);

                  console.log("🧭 [Engagement progressif] Lancement...");
                  const result = generationPr2EngagementProgressif(prepDataEngagement, externalLists);
                  for (const log of result.logs) console.log(log);
                  setPr2Phase2Result(result);
                  setPr2Phase1Result(null);
                  setPr2Phase(2);
                  setPr2CrossCheck(null);
                  try {
                    const extPR1 = externalLists?.pr1Liste || null;
                    const extDig = externalLists?.digestifListe || null;
                    const extRea = externalLists?.reanimationListe || null;
                    const extTho = externalLists?.thoraciqueListe || null;
                    const extOrt = externalLists?.orthopedieListe || null;
                    if (extPR1 || extDig || extRea || extTho || extOrt) {
                      const cc = crossCheckGardesTyped(result.affectations, extPR1, extDig, extRea, extTho, extOrt, {
                        jours: prepDataEngagement.jours,
                        previousExternalLists: externalLists?.previousExternalLists || null,
                      });
                      setPr2CrossCheck(cc);
                    }
                  } catch (err) { console.warn("⚠️ Erreur cross-check Engagement progressif :", err); }
                } catch (err) {
                  console.error("❌ Erreur Engagement progressif :", err);
                  notify("Erreur lors de la génération Engagement progressif : " + (err.message || err));
                }
              }}
            >
              🧭 PR2 — Engagement progressif
            </button>
          )}

          {equilibrageButton}

          {!isAstreinteThoracique && (
            <button
              type="button"
              style={btnMain}
              onClick={() => navigate(withCurrentBack(
                `/responsable/gardes-brute?mois=${encodeURIComponent(MOIS_CLE)}${typeGarde ? `&type=${encodeURIComponent(typeGarde)}` : ""}`
              ))}
            >
              📋 Ouvrir la liste de garde
            </button>
          )}
        </div>
        </>
      )}
    </div>
  );
}






















